/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// React / hooks
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

// @mui material components
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Layout helpers
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Charts
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import FeedChart from "examples/Charts/BarCharts/ReportsBarChart/FeedChart";
import WaterChart from "examples/Charts/LineCharts/ReportsLineChart/WaterChart";
import {
  farmsForUserUrl,
  cyclesByFacilityUrl,
  tempEnvUrl,
  humEnvUrl,
  nh3EnvUrl,
  luxEnvUrl,
  lightingDarkUrl,
  floorTempEnvUrl,
  phEnvUrl,
  waterTempEnvUrl,
  orpEnvUrl,
  performanceKpiUrl,
  performanceWeightUrl,
  performanceFcrUrl,
  performanceActivitiesUrl,
  reportsFeedUrl,
  reportsWaterUrl,
  reportsSummaryUrl,
  reportsPerformanceUrl,
  reportsTopIssuesEnvironmentUrl,
  reportsTopIssuesPerformanceUrl,
  broilerDataByFarmUrl,
  broilersByFarmUrl,
  feedScheduleUrl,
  docDataUrl,
  floorTempEnvDayDetailUrl,
  humEnvDayDetailUrl,
  luxEnvDayDetailUrl,
  nh3EnvDayDetailUrl,
  orpEnvDayDetailUrl,
  phEnvDayDetailUrl,
  tempEnvDayDetailUrl,
  waterTempEnvDayDetailUrl,
} from "api/endpoints";

const createSkipAuthConfig = () => ({ omnioOffline: { skipAuthHeader: true } });
const EMPTY_CHART = { labels: [], datasets: [] };
const IS_DEVELOPMENT = process.env.NODE_ENV === "development";
const extractRows = (response) =>
  Array.isArray(response?.data)
    ? response.data
    : Array.isArray(response?.data?.data)
    ? response.data.data
    : [];
const normalizeFeedReportPayload = (payload) => {
  if (Array.isArray(payload)) {
    return payload.find((entry) => entry?.feed)?.feed || payload[0] || {};
  }
  return payload?.feed && typeof payload.feed === "object" && !Array.isArray(payload.feed)
    ? payload.feed
    : payload || {};
};

const feedChartSeed = {
  labels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
  datasets: [
    {
      label: "Feed (kg)",
      data: [1380, 1415, 1450, 1485, 1500, 1490, 1475],
      backgroundColor: "rgba(45, 206, 137, 0.8)",
      metadata: [
        { fcr: 1.67, target: 1.7, deviationp: -1.4 },
        { fcr: 1.66, target: 1.7, deviationp: -1.9 },
        { fcr: 1.65, target: 1.69, deviationp: -2.3 },
        { fcr: 1.64, target: 1.69, deviationp: -2.9 },
        { fcr: 1.63, target: 1.68, deviationp: -3.0 },
        { fcr: 1.64, target: 1.68, deviationp: -2.3 },
        { fcr: 1.65, target: 1.68, deviationp: -1.8 },
      ],
    },
  ],
};

const DASHBOARD_CACHE_KEY = "OmnioBroilerDashboardCache";
const FACILITY_SELECTION_KEY = "facilityLandingSelections";
const REPORTS_CACHE_KEY = "OmnioReportsCache";
const feedCostReportColumns = [
  { key: "date", label: "Date", width: "2.4cm" },
  { key: "day", label: "Day", width: "1.8cm" },
  { key: "stage", label: "Stage", width: "2.4cm" },
  { key: "feedName", label: "Feed Name", width: "3.1cm" },
  { key: "supplier", label: "Supplier", width: "2.8cm" },
  { key: "weightKg", label: "Weight (Kg)", width: "2.4cm" },
  { key: "bin1", label: "Bin1", width: "2.4cm" },
  { key: "bin2", label: "Bin2", width: "2.4cm" },
  { key: "costExVat", label: "Cost", detail: "(ex VAT)", width: "2.8cm" },
  { key: "costIncVat", label: "Cost", detail: "(inc VAT)", width: "2.8cm" },
];
const docCostReportColumns = [
  { key: "date", label: "Date", width: "2.4cm" },
  { key: "supplier", label: "Supplier", width: "2.8cm" },
  { key: "name", label: "Name", width: "2.8cm" },
  { key: "model", label: "Model", width: "2.4cm" },
  { key: "breedCode", label: "Breed Code", width: "2.6cm" },
  { key: "qty", label: "Qty", width: "1.8cm" },
  { key: "docWeight", label: "DOC Weight", width: "2.6cm" },
  { key: "parentAge", label: "Parent Age", width: "2.6cm" },
  { key: "deformities", label: "Deformities", width: "2.6cm" },
  { key: "costExVat", label: "Cost", detail: "(ex VAT)", width: "2.8cm" },
  { key: "costIncVat", label: "Cost", detail: "(inc VAT)", width: "2.8cm" },
];

const canUseBrowserStorage = () =>
  typeof window !== "undefined" && typeof window.localStorage !== "undefined";

const pickFirst = (...values) =>
  values.find((value) => value !== undefined && value !== null && `${value}`.trim() !== "");

const formatReportNumber = (value) => {
  if (value === undefined || value === null || `${value}`.trim() === "") return "";
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return value;
  return numericValue.toLocaleString("en-ZA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const buildReportFeedOptions = (items, { valueKey = "id", labelKey = "description" } = {}) => {
  const list = [];
  const map = {};

  items.forEach((item) => {
    const rawValue = valueKey === "feedname" ? item.feedname ?? item.id : item.id;
    if (rawValue == null) return;
    const value = String(rawValue);
    if (map[value]) return;

    let rawLabel;
    switch (labelKey) {
      case "value":
        rawLabel = item.value ?? item.description ?? item.feedname ?? item.id;
        break;
      case "feedname":
        rawLabel = item.feedname ?? item.value ?? item.description ?? item.id;
        break;
      default:
        rawLabel = item.description ?? item.value ?? item.feedname ?? item.id;
        break;
    }

    if (rawLabel == null || rawLabel === "") return;
    const label = String(rawLabel);
    list.push({ value, label });
    map[value] = label;
  });

  list.sort((left, right) => left.label.localeCompare(right.label));
  return { list, map };
};

const normalizeReportFeedDictionary = (dictData) => {
  const dict = Array.isArray(dictData)
    ? dictData
    : Array.isArray(dictData?.data)
    ? dictData.data
    : [];
  const valueById = {};
  const descriptionById = {};
  const normalizedDict = dict
    .map((item) => {
      const id = pickFirst(item?.id, item?.ID);
      const type = pickFirst(item?.type, item?.Type);
      if (id == null || type == null) return null;
      const value = pickFirst(item?.value, item?.Value);
      const description = pickFirst(item?.description, item?.Description, item?.name, item?.Name);
      const feedname = pickFirst(item?.feedname, item?.Feedname, item?.feedName, item?.FeedName);
      return {
        id: String(id),
        type: String(type),
        value: value != null ? String(value) : null,
        description: description != null ? String(description) : null,
        feedname: feedname != null ? String(feedname) : null,
      };
    })
    .filter(Boolean);

  normalizedDict.forEach((item) => {
    if (item.value) valueById[item.id] = item.value;
    if (item.description) descriptionById[item.id] = item.description;
  });

  const stageItems = normalizedDict.filter((item) => item.type === "10");
  const supplierItems = normalizedDict.filter((item) => item.type === "11");
  const feedItems = normalizedDict.filter((item) => item.type === "5");

  return {
    stage: buildReportFeedOptions(stageItems, { labelKey: "value" }).map,
    supplier: buildReportFeedOptions(supplierItems, { labelKey: "value" }).map,
    feedName: buildReportFeedOptions(feedItems, { valueKey: "id", labelKey: "value" }).map,
    feedType: buildReportFeedOptions(feedItems, {
      valueKey: "feedname",
      labelKey: "description",
    }).map,
    valueById,
    descriptionById,
  };
};

const resolveFeedMapValue = (value, ...maps) => {
  if (value === undefined || value === null || `${value}`.trim() === "") return "";
  const key = String(value);
  const match = maps.find((map) => map && map[key]);
  return match ? match[key] : key;
};

const mapFeedCostReportRows = (data, feedMaps = {}) => {
  const source = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
  return source.map((row, index) => {
    const id = pickFirst(row?.id, row?.ID) ?? index;
    const stage = pickFirst(row?.stageId);
    const feedName = pickFirst(row?.feedname);
    const supplier = pickFirst(row?.supplierId);
    const weightKg = pickFirst(row?.weight);
    const bin1 = pickFirst(row?.bin1_increase);
    const bin2 = pickFirst(row?.bin2_increase);
    const date = pickFirst(row?.startdate);
    const day = pickFirst(row?.day, row?.Day, row?.cycleDay, row?.CycleDay);
    const costExVat = pickFirst(row?.costExVat);
    const costIncVat = pickFirst(row?.costIncVat);

    return {
      id: String(id),
      date: date != null ? String(date).split("T")[0] : "",
      day: day ?? "",
      stage: resolveFeedMapValue(stage, feedMaps.stage, feedMaps.valueById),
      feedName: resolveFeedMapValue(
        feedName,
        feedMaps.feedName,
        feedMaps.valueById,
        feedMaps.feedType,
        feedMaps.descriptionById
      ),
      supplier: resolveFeedMapValue(supplier, feedMaps.supplier, feedMaps.valueById),
      weightKg: weightKg,
      bin1: bin1,
      bin2: bin2,
      costExVat: costExVat,
      costIncVat: costIncVat,
    };
  });
};

const mapDocCostReportRows = (data) => {
  const source = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
  return source.map((row, index) => {
    const id = pickFirst(row?.id, row?.ID) ?? index;
    const batchDate = pickFirst(row?.batchDate, row?.BatchDate);

    return {
      id: String(id),
      date: batchDate != null ? String(batchDate).split("T")[0] : "",
      supplier: pickFirst(row?.supplier, row?.Supplier) ?? "",
      name: pickFirst(row?.name, row?.Name) ?? "",
      model: pickFirst(row?.model, row?.Model) ?? "",
      breedCode: pickFirst(row?.breederCode, row?.BreederCode) ?? "",
      qty: pickFirst(row?.qauntity, row?.quantity, row?.Quantity) ?? "",
      docWeight: pickFirst(row?.chickWeight, row?.ChickWeight) ?? "",
      parentAge: pickFirst(row?.parentAge, row?.ParentAge) ?? "",
      deformities: pickFirst(row?.deformaties, row?.deformities, row?.Deformities) ?? "",
      costExVat: pickFirst(row?.costExVat, row?.CostExVat) ?? "",
      costIncVat: pickFirst(row?.costIncVat, row?.CostIncVat) ?? "",
    };
  });
};

const readReportsCache = () => {
  if (!canUseBrowserStorage()) return null;
  const raw = window.localStorage.getItem(REPORTS_CACHE_KEY);
  if (!raw) return { byFacility: {}, timestamp: Date.now() };
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      if (!parsed.byFacility || typeof parsed.byFacility !== "object") {
        parsed.byFacility = {};
      }
      return parsed;
    }
  } catch (error) {
    console.warn("Unable to parse reports cache", error);
  }
  return { byFacility: {}, timestamp: Date.now() };
};

const writeReportsCache = (cache) => {
  if (!canUseBrowserStorage() || !cache) return;
  try {
    window.localStorage.setItem(REPORTS_CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    console.warn("Unable to persist reports cache", error);
  }
};

const updateReportsCacheEntry = (facilityId, cycleId, partial) => {
  if (!facilityId || !cycleId || !partial) return;
  const cache = readReportsCache();
  const facilityKey = `${facilityId}`;
  const cycleKey = `${cycleId}`;
  const facilityCache = cache.byFacility[facilityKey] || {};
  const existing = facilityCache[cycleKey] || {};
  facilityCache[cycleKey] = {
    ...existing,
    ...partial,
    lastUpdated: Date.now(),
  };
  cache.byFacility[facilityKey] = facilityCache;
  cache.timestamp = Date.now();
  writeReportsCache(cache);
};

const readReportsCacheEntry = (facilityId, cycleId) => {
  if (!facilityId || !cycleId) return null;
  const cache = readReportsCache();
  return cache.byFacility?.[`${facilityId}`]?.[`${cycleId}`] || null;
};

function Reports() {
  const location = useLocation();
  const [isOffline, setIsOffline] = useState(
    () => typeof navigator !== "undefined" && navigator.onLine === false
  );
  const [facilityOptions, setFacilityOptions] = useState([]);
  const [cycleOptions, setCycleOptions] = useState([]);
  const [selectedFacilityId, setSelectedFacilityId] = useState("");
  const dropdownRequestVersion = useRef(0);
  const [selectedCycleId, setSelectedCycleId] = useState("");
  const [facilityData, setFacilityData] = useState({});
  const [farmOptions, setFarmOptions] = useState([]);
  const [selectedFarmId, setSelectedFarmId] = useState("");
  const [envTempChartData, setEnvTempChartData] = useState(EMPTY_CHART);
  const [lightingChartData, setLightingChartData] = useState(EMPTY_CHART);
  const [lightingSeries, setLightingSeries] = useState([]);
  const [lightingDarkSeries, setLightingDarkSeries] = useState([]);
  const [isLoadingEnvTemp, setIsLoadingEnvTemp] = useState(false);
  const [envMinDeviation, setEnvMinDeviation] = useState(0);
  const [envMinDeviationDefaultEnabled, setEnvMinDeviationDefaultEnabled] = useState(false);
  const [envSeries, setEnvSeries] = useState(null);
  const [envCheckedItems, setEnvCheckedItems] = useState({
    "Temp, Hum, NH3": true,
    "Floor Temp": true,
  });
  const [waterChartData, setWaterChartData] = useState(EMPTY_CHART);
  const [envTempPopupOpen, setEnvTempPopupOpen] = useState(false);
  const [envTempPopupTitle, setEnvTempPopupTitle] = useState("");
  const [envTempPopupChartData, setEnvTempPopupChartData] = useState(EMPTY_CHART);
  const [isLoadingEnvTempPopup, setIsLoadingEnvTempPopup] = useState(false);
  const [envTempPopupError, setEnvTempPopupError] = useState("");
  const [isLoadingWaterQuality, setIsLoadingWaterQuality] = useState(false);
  const [waterMinDeviation, setWaterMinDeviation] = useState(0);
  const [waterMinDeviationDefaultEnabled, setWaterMinDeviationDefaultEnabled] = useState(false);
  const [waterSeries, setWaterSeries] = useState(null);
  const [lightingMinDeviation, setLightingMinDeviation] = useState(0);
  const [lightingMinDeviationDefaultEnabled, setLightingMinDeviationDefaultEnabled] =
    useState(false);
  const [lightingCheckedItems, setLightingCheckedItems] = useState({
    Lux: true,
    "Dark Period": true,
  });
  const [feedChartData, setFeedChartData] = useState(feedChartSeed);
  const [feedTotal, setFeedTotal] = useState(
    feedChartSeed.datasets?.[0]?.data?.reduce((sum, val) => sum + (Number(val) || 0), 0)
  );
  const [feedCostRows, setFeedCostRows] = useState([]);
  const [sortedFeedCostRows, setSortedFeedCostRows] = useState([]);
  const [feedCostOrderBy, setFeedCostOrderBy] = useState("date");
  const [feedCostOrder, setFeedCostOrder] = useState("asc");
  const [isLoadingFeedCosts, setIsLoadingFeedCosts] = useState(false);
  const [feedCostsError, setFeedCostsError] = useState("");
  const [docCostRows, setDocCostRows] = useState([]);
  const [docCostOrder, setDocCostOrder] = useState("asc");
  const [isLoadingDocCosts, setIsLoadingDocCosts] = useState(false);
  const [docCostsError, setDocCostsError] = useState("");
  const [feedCostMaps, setFeedCostMaps] = useState({
    stage: {},
    supplier: {},
    feedName: {},
    feedType: {},
    valueById: {},
    descriptionById: {},
  });
  const [topIssuesEnvironmentChartData, setTopIssuesEnvironmentChartData] = useState(EMPTY_CHART);

  const getFeedCostSortValue = (row, orderByKey) => {
    const value = row?.[orderByKey];
    if (orderByKey === "date") {
      if (!value) return -Infinity;
      const time = Date.parse(value);
      return Number.isFinite(time) ? time : String(value).toLowerCase();
    }
    if (value === undefined || value === null || value === "") return "";
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : String(value).toLowerCase();
  };

  const handleFeedCostRequestSort = (key) => {
    const isAsc = feedCostOrderBy === key && feedCostOrder === "asc";
    setFeedCostOrder(isAsc ? "desc" : "asc");
    setFeedCostOrderBy(key);
  };

  useEffect(() => {
    setSortedFeedCostRows((rows) => {
      const sorted = [...rows].sort((a, b) => {
        const left = getFeedCostSortValue(a, feedCostOrderBy);
        const right = getFeedCostSortValue(b, feedCostOrderBy);
        if (left < right) return feedCostOrder === "asc" ? -1 : 1;
        if (left > right) return feedCostOrder === "asc" ? 1 : -1;
        return 0;
      });
      return sorted;
    });
  }, [feedCostOrder, feedCostOrderBy]);

  useEffect(() => {
    setSortedFeedCostRows((rows) => {
      const sorted = [...feedCostRows].sort((a, b) => {
        const left = getFeedCostSortValue(a, feedCostOrderBy);
        const right = getFeedCostSortValue(b, feedCostOrderBy);
        if (left < right) return feedCostOrder === "asc" ? -1 : 1;
        if (left > right) return feedCostOrder === "asc" ? 1 : -1;
        return 0;
      });
      return sorted;
    });
  }, [feedCostRows, feedCostOrder, feedCostOrderBy]);

  const feedCostTotalExVat = useMemo(() => {
    const values = sortedFeedCostRows.map((row) => Number(row.costExVat));
    return values.reduce((sum, value) => sum + (Number.isFinite(value) ? value : 0), 0);
  }, [sortedFeedCostRows]);

  const feedCostTotalIncVat = useMemo(() => {
    const values = sortedFeedCostRows.map((row) => Number(row.costIncVat));
    return values.reduce((sum, value) => sum + (Number.isFinite(value) ? value : 0), 0);
  }, [sortedFeedCostRows]);

  const sortedDocCostRows = useMemo(() => {
    const rows = [...docCostRows];
    rows.sort((left, right) => {
      const leftDate = Date.parse(left?.date);
      const rightDate = Date.parse(right?.date);
      const result =
        Number.isFinite(leftDate) && Number.isFinite(rightDate)
          ? leftDate - rightDate
          : String(left?.date ?? "").localeCompare(String(right?.date ?? ""));
      return docCostOrder === "asc" ? result : -result;
    });
    return rows;
  }, [docCostOrder, docCostRows]);

  const docCostTotalExVat = useMemo(
    () =>
      sortedDocCostRows.reduce((sum, row) => {
        const value = Number(row?.costExVat);
        return sum + (Number.isFinite(value) ? value : 0);
      }, 0),
    [sortedDocCostRows]
  );

  const docCostTotalIncVat = useMemo(
    () =>
      sortedDocCostRows.reduce((sum, row) => {
        const value = Number(row?.costIncVat);
        return sum + (Number.isFinite(value) ? value : 0);
      }, 0),
    [sortedDocCostRows]
  );

  const [topIssuesPerformanceChartData, setTopIssuesPerformanceChartData] = useState(EMPTY_CHART);
  const [waterConsumptionChartData, setWaterConsumptionChartData] = useState(EMPTY_CHART);
  const [waterConsumptionTotal, setWaterConsumptionTotal] = useState(null);
  const [summaryRows, setSummaryRows] = useState([]);
  const [summaryMinDeviation, setSummaryMinDeviation] = useState(0);
  const [summaryMinDeviationDefaultEnabled, setSummaryMinDeviationDefaultEnabled] = useState(false);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [summaryOrderBy, setSummaryOrderBy] = useState("recordDate");
  const [summaryOrder, setSummaryOrder] = useState("asc");
  const [performanceTableRows, setPerformanceTableRows] = useState([]);
  const [isLoadingPerformanceTable, setIsLoadingPerformanceTable] = useState(false);
  const [performanceTableOrderBy, setPerformanceTableOrderBy] = useState("recordDate");
  const [performanceTableOrder, setPerformanceTableOrder] = useState("asc");
  const [waterCheckedItems, setWaterCheckedItems] = useState({
    "Water Temp": true,
    pH: true,
    ORP: true,
  });
  const [performanceChartData, setPerformanceChartData] = useState(EMPTY_CHART);
  const [isLoadingPerformance, setIsLoadingPerformance] = useState(false);
  const [performanceMinDeviation, setPerformanceMinDeviation] = useState(0);
  const [performanceMinDeviationDefaultEnabled, setPerformanceMinDeviationDefaultEnabled] =
    useState(false);
  const [performanceSeries, setPerformanceSeries] = useState(null);
  const [performanceWeightFcrChartData, setPerformanceWeightFcrChartData] = useState(EMPTY_CHART);
  const [performanceWeightChartData, setPerformanceWeightChartData] = useState(EMPTY_CHART);
  const [performanceFcrChartData, setPerformanceFcrChartData] = useState(EMPTY_CHART);
  const [isLoadingPerformanceWeightFcr, setIsLoadingPerformanceWeightFcr] = useState(false);
  const [performanceWeightFcrSeries, setPerformanceWeightFcrSeries] = useState(null);
  const [activitiesChartData, setActivitiesChartData] = useState(EMPTY_CHART);
  const [isLoadingActivities, setIsLoadingActivities] = useState(false);
  const [activitiesStatusFilters, setActivitiesStatusFilters] = useState({
    Completed: true,
    "Conditions not met": true,
    "Not completed": true,
  });
  const [activitiesPayload, setActivitiesPayload] = useState(null);
  const [performanceMeta, setPerformanceMeta] = useState({
    live: null,
    cumulative: null,
    startDate: "",
    endDate: "",
  });
  const envScrollRef = useRef(null);
  const envPanelRefs = useRef([]);
  const perfScrollRef = useRef(null);
  const perfPanelRefs = useRef([]);
  const localInsightsAllowedFacilityRef = useRef("");
  const [activeEnvPanel, setActiveEnvPanel] = useState(0);
  const [activePerfPanel, setActivePerfPanel] = useState(0);
  const [activeReportSection, setActiveReportSection] = useState("Performance");
  const [localInsightsMessage, setLocalInsightsMessage] = useState("");
  const [localInsightsAllowed, setLocalInsightsAllowed] = useState(null);
  const [localInsightsFacilityId, setLocalInsightsFacilityId] = useState("");
  const navState = location.state || {};
  const navFacilityId =
    navState?.facilityId !== undefined && navState?.facilityId !== null
      ? `${navState.facilityId}`
      : null;
  const facilityFromQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const facility = params.get("facility");
    return facility ? `${facility}` : null;
  }, [location.search]);
  const farmFromQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const farm = params.get("farm");
    return farm ? `${farm}` : null;
  }, [location.search]);
  const cycleFromQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const cycle = params.get("cycle");
    return cycle ? `${cycle}` : null;
  }, [location.search]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const canLoadReportData =
    (IS_DEVELOPMENT && Boolean(selectedFacilityId)) ||
    (localInsightsAllowed === true && `${localInsightsFacilityId}` === `${selectedFacilityId}`);
  const canLoadSelectedReportData = useCallback(
    () =>
      canLoadReportData ||
      (IS_DEVELOPMENT && Boolean(selectedFacilityId)) ||
      localInsightsAllowedFacilityRef.current === `${selectedFacilityId}`,
    [canLoadReportData, selectedFacilityId]
  );

  useEffect(() => {
    if (!canLoadSelectedReportData()) {
      setFeedCostMaps({
        stage: {},
        supplier: {},
        feedName: {},
        feedType: {},
        valueById: {},
        descriptionById: {},
      });
      return undefined;
    }

    if (!selectedFarmId) {
      setFeedCostMaps({
        stage: {},
        supplier: {},
        feedName: {},
        feedType: {},
        valueById: {},
        descriptionById: {},
      });
      return undefined;
    }

    let didCancel = false;

    const loadFeedDictionary = async () => {
      try {
        const response = await axios.get(broilerDataByFarmUrl(selectedFarmId));
        if (!didCancel) setFeedCostMaps(normalizeReportFeedDictionary(response.data));
      } catch (error) {
        console.error("Failed to load report feed dictionary", error);
        if (!didCancel) {
          setFeedCostMaps({
            stage: {},
            supplier: {},
            feedName: {},
            feedType: {},
            valueById: {},
            descriptionById: {},
          });
        }
      }
    };

    loadFeedDictionary();

    return () => {
      didCancel = true;
    };
  }, [canLoadSelectedReportData, selectedFarmId]);

  const loadFeedCosts = useCallback(async () => {
    if (!canLoadSelectedReportData() || !selectedFacilityId || !selectedCycleId) {
      setFeedCostRows([]);
      setFeedCostsError("");
      setIsLoadingFeedCosts(false);
      return;
    }

    setIsLoadingFeedCosts(true);
    setFeedCostsError("");
    try {
      const response = await axios.get(feedScheduleUrl(selectedFacilityId, selectedCycleId));
      setFeedCostRows(mapFeedCostReportRows(response.data, feedCostMaps));
    } catch (error) {
      console.error("Failed to load report feed costs", error);
      setFeedCostRows([]);
      setFeedCostsError("Failed to load feed costs.");
    } finally {
      setIsLoadingFeedCosts(false);
    }
  }, [canLoadSelectedReportData, feedCostMaps, selectedCycleId, selectedFacilityId]);

  useEffect(() => {
    loadFeedCosts();
  }, [loadFeedCosts]);

  const loadDocCosts = useCallback(async () => {
    if (!canLoadSelectedReportData() || !selectedFacilityId || !selectedCycleId) {
      setDocCostRows([]);
      setDocCostsError("");
      setIsLoadingDocCosts(false);
      return;
    }

    setIsLoadingDocCosts(true);
    setDocCostsError("");
    try {
      const response = await axios.get(docDataUrl(selectedFacilityId, selectedCycleId));
      setDocCostRows(mapDocCostReportRows(response.data));
    } catch (error) {
      console.error("Failed to load report DOC costs", error);
      setDocCostRows([]);
      setDocCostsError("Failed to load DOC costs.");
    } finally {
      setIsLoadingDocCosts(false);
    }
  }, [canLoadSelectedReportData, selectedCycleId, selectedFacilityId]);

  useEffect(() => {
    loadDocCosts();
  }, [loadDocCosts]);

  const checkLocalInsightsSubscription = useCallback(async () => {
    if (!selectedFarmId || !selectedFacilityId) {
      setLocalInsightsAllowed(null);
      setLocalInsightsFacilityId("");
      localInsightsAllowedFacilityRef.current = "";
      return false;
    }

    if (IS_DEVELOPMENT) {
      setLocalInsightsAllowed(true);
      setLocalInsightsFacilityId(`${selectedFacilityId}`);
      localInsightsAllowedFacilityRef.current = `${selectedFacilityId}`;
      return true;
    }

    try {
      const response = await axios.get(broilersByFarmUrl(selectedFarmId));
      const rows = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.data?.data)
        ? response.data.data
        : [];
      const selectedFacility = rows.find((row) => `${row?.id}` === `${selectedFacilityId}`);
      const localInsightsValue = selectedFacility?.localInsights;
      const isSubscribed =
        localInsightsValue === true || `${localInsightsValue}`.toLowerCase() === "true";

      setLocalInsightsFacilityId(`${selectedFacilityId}`);
      setLocalInsightsAllowed(Boolean(selectedFacility && isSubscribed));
      localInsightsAllowedFacilityRef.current =
        selectedFacility && isSubscribed ? `${selectedFacilityId}` : "";
      if (selectedFacility && !isSubscribed) {
        setLocalInsightsMessage(
          `Please subscribe ${selectedFacility.name} to Local Insights to view Reports`
        );
      }
      return Boolean(selectedFacility && isSubscribed);
    } catch (error) {
      console.error("Failed to check Local Insights subscription", error);
      setLocalInsightsAllowed(null);
      setLocalInsightsFacilityId("");
      localInsightsAllowedFacilityRef.current = "";
      return false;
    }
  }, [selectedFacilityId, selectedFarmId]);

  useEffect(() => {
    if (!selectedFarmId || !selectedFacilityId) return;
    checkLocalInsightsSubscription();
  }, [checkLocalInsightsSubscription, selectedFacilityId, selectedFarmId]);

  const normalizeDateLabel = (value) => {
    if (!value) return "";
    const asString = String(value);
    const dateOnly = asString.split("T")[0];
    const d = new Date(dateOnly);
    return Number.isNaN(d.getTime()) ? dateOnly : d.toISOString().split("T")[0];
  };

  const formatPopupDateTime = (value) => {
    if (!value) return "";
    const raw = String(value).trim();
    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/);
    if (match) return `${match[1]}-${match[2]}-${match[3]} ${match[4]}:${match[5]}`;
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return raw;
    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    const day = String(parsed.getDate()).padStart(2, "0");
    const hour = String(parsed.getHours()).padStart(2, "0");
    const minute = String(parsed.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day} ${hour}:${minute}`;
  };

  const formatPopupAxisLabel = (value) => {
    const formatted = formatPopupDateTime(value);
    const match = formatted.match(/\b(\d{2}:\d{2})$/);
    return match ? match[1] : formatted;
  };

  const getDateSortKey = (value) => {
    if (!value) return null;
    const raw = String(value).trim();
    const dateOnly = raw.split("T")[0].split(" ")[0];

    const ymdDash = dateOnly.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (ymdDash) return `${ymdDash[1]}${ymdDash[2]}${ymdDash[3]}`;

    const dmyDash = dateOnly.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (dmyDash) return `${dmyDash[3]}${dmyDash[2]}${dmyDash[1]}`;

    const mdySlash = dateOnly.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (mdySlash) {
      const month = String(Number(mdySlash[1])).padStart(2, "0");
      const day = String(Number(mdySlash[2])).padStart(2, "0");
      return `${mdySlash[3]}${month}${day}`;
    }

    const normalized = normalizeDateLabel(raw);
    const normalizedMatch = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (normalizedMatch) {
      return `${normalizedMatch[1]}${normalizedMatch[2]}${normalizedMatch[3]}`;
    }

    return null;
  };

  const formatDateToDdMmm = (value) => {
    if (!value) return "";
    const raw = String(value).trim();
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const dmyDash = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (dmyDash) {
      const monthIndex = Number(dmyDash[2]) - 1;
      if (monthIndex >= 0 && monthIndex < monthNames.length) {
        return `${dmyDash[1]}-${monthNames[monthIndex]}`;
      }
    }

    const ymdDash = raw.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|T|\s)/);
    if (ymdDash) {
      const monthIndex = Number(ymdDash[2]) - 1;
      if (monthIndex >= 0 && monthIndex < monthNames.length) {
        return `${ymdDash[3]}-${monthNames[monthIndex]}`;
      }
    }

    const mdySlash = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s|$)/);
    if (mdySlash) {
      const monthIndex = Number(mdySlash[1]) - 1;
      const day = String(Number(mdySlash[2])).padStart(2, "0");
      if (monthIndex >= 0 && monthIndex < monthNames.length) {
        return `${day}-${monthNames[monthIndex]}`;
      }
    }

    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) {
      const day = String(parsed.getDate()).padStart(2, "0");
      return `${day}-${monthNames[parsed.getMonth()]}`;
    }
    return raw;
  };

  const statusToColor = (status) => {
    switch (`${status || ""}`.toLowerCase()) {
      case "green":
        return "#66bb6a";
      case "orange":
      case "amber":
      case "yellow":
        return "#f39507";
      case "red":
        return "#d32f2f";
      default:
        return "#9e9e9e";
    }
  };

  const roundTo1 = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? Math.round(num * 10) / 10 : null;
  };

  const withAlpha = (color, alpha = 1) => {
    if (typeof color !== "string") return color;
    const normalized = color.trim();
    if (!normalized.startsWith("#")) return color;
    const hex = normalized.slice(1);
    const safeAlpha = Math.max(0, Math.min(1, Number(alpha)));
    const toNumber = (v) => Number.parseInt(v, 16);
    if (hex.length === 3) {
      const r = toNumber(`${hex[0]}${hex[0]}`);
      const g = toNumber(`${hex[1]}${hex[1]}`);
      const b = toNumber(`${hex[2]}${hex[2]}`);
      return `rgba(${r}, ${g}, ${b}, ${safeAlpha})`;
    }
    if (hex.length === 6) {
      const r = toNumber(hex.slice(0, 2));
      const g = toNumber(hex.slice(2, 4));
      const b = toNumber(hex.slice(4, 6));
      return `rgba(${r}, ${g}, ${b}, ${safeAlpha})`;
    }
    return color;
  };

  const normalizeMinDeviation = (value) => {
    if (value === "" || value === null || value === undefined) return 0;
    const num = Number(value);
    return Number.isFinite(num) ? Math.round(num) : 0;
  };

  const buildEnvSeries = (raw, prefix) => {
    const dateArray = Array.isArray(raw?.[`${prefix}_recordDate`])
      ? raw[`${prefix}_recordDate`]
      : [];
    const dayArray = Array.isArray(raw?.day) ? raw.day : [];
    const sourceArray = dayArray.length ? dayArray : dateArray;
    return sourceArray
      .map((dayValue, idx) => {
        const hasDay = dayArray[idx] !== null && dayArray[idx] !== undefined;
        const label = hasDay ? String(dayValue) : normalizeDateLabel(dateArray[idx]);
        const value = roundTo1(raw?.[`${prefix}_avgVal`]?.[idx]);
        if (!label || !Number.isFinite(value)) return null;
        const lowTarget = roundTo1(raw?.[`${prefix}_targetL`]?.[idx]);
        const highTarget = roundTo1(raw?.[`${prefix}_targetH`]?.[idx]);
        return {
          label,
          day: hasDay ? dayArray[idx] : null,
          date: dateArray[idx] ?? null,
          value,
          status: raw?.[`${prefix}_status`]?.[idx] ?? raw?.status?.[idx],
          avgDeviationP: roundTo1(raw?.[`${prefix}_avgDeviationP`]?.[idx]),
          lowVal: roundTo1(raw?.[`${prefix}_lowVal`]?.[idx]),
          lowTarget,
          highVal: roundTo1(raw?.[`${prefix}_highVal`]?.[idx]),
          highTarget,
          target: [lowTarget, highTarget],
        };
      })
      .filter(Boolean);
  };

  const buildEnvDetailPopupChart = (rows, config) => {
    const { metricKey, metricLabel, chartLabel, unit, borderColor, backgroundColor } = config;
    const popupDeviationToColor = (deviationP) => {
      const numericDeviation = Number(deviationP);
      if (!Number.isFinite(numericDeviation)) return "#9e9e9e";
      if (numericDeviation === 0) return "#66bb6a";
      if (numericDeviation <= 15) return "#f39507";
      return "#d32f2f";
    };
    const normalizeOptionalRoundedValue = (rawValue) => {
      if (rawValue === null || rawValue === undefined) return null;
      const asString = String(rawValue).trim().toLowerCase();
      if (asString === "" || asString === "null" || asString === "undefined") return null;
      return roundTo1(rawValue);
    };

    const points = (Array.isArray(rows) ? rows : [])
      .map((entry) => {
        const sensorKey = Object.keys(entry || {}).find(
          (key) => key !== "timestamp" && key !== "timestamp_name"
        );
        const payload = sensorKey ? entry?.[sensorKey]?.[metricKey] : null;
        const value = roundTo1(payload?.sensor);
        if (!Number.isFinite(value)) return null;
        const rawTarget = payload?.target;
        const hasValidTargetArray =
          Array.isArray(rawTarget) &&
          rawTarget.some((item) => normalizeOptionalRoundedValue(item) !== null);
        const hasValidSingleTarget =
          !Array.isArray(rawTarget) && normalizeOptionalRoundedValue(rawTarget) !== null;
        const hasValidTarget = hasValidTargetArray || hasValidSingleTarget;
        const target = hasValidTargetArray
          ? rawTarget.map((item) => normalizeOptionalRoundedValue(item))
          : hasValidSingleTarget
          ? [normalizeOptionalRoundedValue(rawTarget)]
          : "N/A";
        return {
          label: formatPopupAxisLabel(entry?.timestamp_name),
          value,
          deviationP: payload?.deviationP != null ? Number(payload.deviationP) : null,
          color: hasValidTarget
            ? payload?.deviationP != null
              ? popupDeviationToColor(payload.deviationP)
              : "#9e9e9e"
            : "#9e9e9e",
          target,
          timestampName: entry?.timestamp_name ?? "",
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        const da = Date.parse(a.timestampName);
        const db = Date.parse(b.timestampName);
        if (Number.isNaN(da) || Number.isNaN(db)) {
          return `${a.timestampName || ""}`.localeCompare(`${b.timestampName || ""}`);
        }
        return da - db;
      });

    return {
      labels: points.map((point) => point.label),
      datasets: [
        {
          type: "line",
          label: chartLabel,
          data: points.map((point) => point.value),
          borderColor,
          backgroundColor,
          pointBackgroundColor: points.map((point) => point.color),
          pointBorderColor: points.map((point) => point.color),
          pointRadius: 4,
          pointHoverRadius: 6,
          fill: false,
          tension: 0.3,
          showLegend: true,
          metadata: points.map((point) => ({
            tooltipType: "envDetailPopup",
            metricLabel,
            unit,
            deviationP: point.deviationP,
            target: point.target,
            timestamp_name: point.timestampName,
          })),
          datalabels: { display: false },
        },
      ],
    };
  };

  const buildLightingDarkSeries = (raw) => {
    const rows = Array.isArray(raw) ? raw : null;
    const darkDates = rows
      ? rows.map((row) => row?.record_date)
      : Array.isArray(raw?.dark_date)
      ? raw.dark_date
      : [];
    const days = rows ? rows.map((row) => row?.cycle_day) : Array.isArray(raw?.day) ? raw.day : [];
    const starts = rows
      ? rows.map((row) => row?.start)
      : Array.isArray(raw?.dark_start)
      ? raw.dark_start
      : [];
    const ends = rows
      ? rows.map((row) => row?.end)
      : Array.isArray(raw?.dark_end)
      ? raw.dark_end
      : [];
    const periods = rows
      ? rows.map((row) => row?.value)
      : Array.isArray(raw?.dark_period)
      ? raw.dark_period
      : [];
    const statuses = rows
      ? rows.map((row) => row?.status)
      : Array.isArray(raw?.dark_status)
      ? raw.dark_status
      : [];
    const deviations = rows
      ? rows.map((row) => row?.deviationp)
      : Array.isArray(raw?.dark_deviationp)
      ? raw.dark_deviationp
      : [];
    const targets = rows
      ? rows.map((row) => row?.target)
      : Array.isArray(raw?.dark_target)
      ? raw.dark_target
      : [];
    const byLabel = new Map();

    const parseDarkDateLabel = (value) => {
      if (!value) return "";
      const rawValue = String(value).trim();
      const dmyMatch = rawValue.match(/^(\d{2})-(\d{2})-(\d{4})$/);
      if (dmyMatch) {
        const isoCandidate = `${dmyMatch[3]}-${dmyMatch[2]}-${dmyMatch[1]}`;
        return normalizeDateLabel(isoCandidate);
      }
      return normalizeDateLabel(rawValue);
    };

    darkDates.forEach((darkDateValue, idx) => {
      if (!darkDateValue) return;
      const hasDay = days[idx] !== null && days[idx] !== undefined;
      const label = hasDay ? String(days[idx]) : parseDarkDateLabel(darkDateValue);
      if (!label) return;
      const darkPeriod = Number(periods[idx]);
      const startRaw = starts[idx];
      const endRaw = ends[idx];
      const startValue =
        startRaw === null ||
        startRaw === undefined ||
        `${startRaw}`.trim().toLowerCase() === "null" ||
        `${startRaw}`.trim() === ""
          ? null
          : String(startRaw);
      const endValue =
        endRaw === null ||
        endRaw === undefined ||
        `${endRaw}`.trim().toLowerCase() === "null" ||
        `${endRaw}`.trim() === ""
          ? null
          : String(endRaw);
      const nextPoint = {
        label,
        day: hasDay ? days[idx] : null,
        date: darkDateValue,
        dark_start: startValue,
        dark_end: endValue,
        dark_period: Number.isFinite(darkPeriod) ? darkPeriod : null,
        status: statuses[idx] ?? null,
        dark_deviationp: Number.isFinite(Number(deviations[idx])) ? Number(deviations[idx]) : null,
        dark_target: Number.isFinite(Number(targets[idx])) ? Number(targets[idx]) : null,
      };
      const existing = byLabel.get(label);
      if (!existing) {
        byLabel.set(label, nextPoint);
        return;
      }
      const existingPeriod = Number(existing.dark_period);
      const candidatePeriod = Number(nextPoint.dark_period);
      const chooseCandidate =
        Number.isFinite(candidatePeriod) &&
        (!Number.isFinite(existingPeriod) || candidatePeriod >= existingPeriod);
      if (chooseCandidate) byLabel.set(label, nextPoint);
    });

    return Array.from(byLabel.values());
  };

  const buildLightingChartData = (luxPoints, darkPoints, threshold, toggles) => {
    if (!Array.isArray(luxPoints)) {
      setLightingChartData(EMPTY_CHART);
      return;
    }

    const luxEnabled = toggles?.Lux !== false;
    const darkEnabled = toggles?.["Dark Period"] !== false;
    const filteredLux = luxEnabled ? filterByDeviation(luxPoints, threshold) : [];
    const sortedLux = [...filteredLux].sort((a, b) => {
      const leftDay = Number(a?.label);
      const rightDay = Number(b?.label);
      if (Number.isFinite(leftDay) && Number.isFinite(rightDay)) return leftDay - rightDay;
      const da = Date.parse(a?.label);
      const db = Date.parse(b?.label);
      if (Number.isNaN(da) || Number.isNaN(db)) {
        return `${a?.label || ""}`.localeCompare(`${b?.label || ""}`);
      }
      return da - db;
    });

    const effectiveDarkPoints = darkEnabled && Array.isArray(darkPoints) ? darkPoints : [];
    const darkByLabel = new Map(effectiveDarkPoints.map((pt) => [pt.label, pt]));
    const darkLabels = effectiveDarkPoints.map((pt) => pt.label);
    const labels = Array.from(new Set([...sortedLux.map((pt) => pt.label), ...darkLabels])).sort(
      (a, b) => {
        const leftDay = Number(a);
        const rightDay = Number(b);
        if (Number.isFinite(leftDay) && Number.isFinite(rightDay)) return leftDay - rightDay;
        const da = Date.parse(a);
        const db = Date.parse(b);
        if (Number.isNaN(da) || Number.isNaN(db)) return `${a || ""}`.localeCompare(`${b || ""}`);
        return da - db;
      }
    );

    const luxByLabel = new Map(sortedLux.map((pt) => [pt.label, pt]));
    const lineData = labels.map((lbl) => luxByLabel.get(lbl)?.value ?? null);
    const pointColors = labels.map((lbl) => statusToColor(luxByLabel.get(lbl)?.status));
    const lineMetadata = labels.map((lbl) => {
      const pt = luxByLabel.get(lbl);
      return {
        tooltipType: "lighting",
        day: pt?.day ?? null,
        date: pt?.date ?? null,
        lux_avgDeviationP: pt?.avgDeviationP ?? null,
        lux_lowVal: pt?.lowVal ?? null,
        lux_highVal: pt?.highVal ?? null,
        lux_targetL: pt?.lowTarget ?? null,
        lux_targetH: pt?.highTarget ?? null,
      };
    });

    const darkStartBars = labels.map((lbl) => {
      const period = Number(darkByLabel.get(lbl)?.dark_period);
      return Number.isFinite(period) ? period : null;
    });
    const darkBarColors = labels.map((lbl) => statusToColor(darkByLabel.get(lbl)?.status));
    const darkStartMetadata = labels.map((lbl) => {
      const dark = darkByLabel.get(lbl);
      return dark
        ? {
            tooltipType: "lightingDarkBar",
            day: dark.day ?? null,
            date: dark.date ?? null,
            dark_start: dark.dark_start,
            dark_end: dark.dark_end,
            dark_period: dark.dark_period,
            dark_status: dark.status,
            dark_deviationp: dark.dark_deviationp,
            dark_target: dark.dark_target,
          }
        : {};
    });
    setLightingChartData({
      labels,
      datasets: [
        darkEnabled && {
          type: "bar",
          label: "Dark Start",
          data: darkStartBars,
          yAxisID: "y",
          backgroundColor: darkBarColors.map((color) => withAlpha(color, 0.35)),
          borderColor: darkBarColors,
          borderWidth: 1,
          barThickness: 18,
          maxBarThickness: 18,
          showLegend: true,
          metadata: darkStartMetadata,
          datalabels: {
            display: true,
            anchor: "end",
            align: "top",
            offset: 2,
            color: "#fff",
            font: { weight: "bold", size: 11 },
            formatter: (value, context) => {
              const period = Number(context?.dataset?.metadata?.[context.dataIndex]?.dark_period);
              if (!Number.isFinite(period)) return "";
              return Number.isInteger(period) ? `${period}` : period.toFixed(1);
            },
          },
          order: 1,
        },
        {
          type: "line",
          label: "Avg Lux",
          data: lineData,
          yAxisID: "y1",
          borderColor: "#00d1ff",
          backgroundColor: "rgba(0, 209, 255, 0.2)",
          pointBackgroundColor: pointColors,
          pointBorderColor: pointColors,
          pointRadius: 4,
          pointHoverRadius: 6,
          fill: false,
          tension: 0.3,
          showLegend: true,
          metadata: lineMetadata,
          datalabels: { display: false },
          order: 3,
        },
      ].filter(Boolean),
    });
  };

  const filterByDeviation = (points, threshold) => {
    const limit = Number(threshold);
    if (!Number.isFinite(limit) || limit <= 0) return points;
    return points.filter((pt) => Number.isFinite(pt.avgDeviationP) && pt.avgDeviationP > limit);
  };

  const buildEnvChartData = (series, threshold, toggles) => {
    if (!series) {
      setEnvTempChartData(EMPTY_CHART);
      return;
    }
    const { tempPoints = [], humPoints = [], nh3Points = [], ftempPoints = [] } = series;
    const tempGroupEnabled = toggles?.["Temp, Hum, NH3"] !== false;
    const ftempEnabled = toggles?.["Floor Temp"] !== false;
    const filteredTemp = tempGroupEnabled ? filterByDeviation(tempPoints, threshold) : [];
    const filteredHum = tempGroupEnabled ? filterByDeviation(humPoints, threshold) : [];
    const filteredNh3 = tempGroupEnabled ? filterByDeviation(nh3Points, threshold) : [];
    const filteredFtemp = ftempEnabled ? filterByDeviation(ftempPoints, threshold) : [];

    const mergedLabels = Array.from(
      new Set([
        ...filteredTemp.map((pt) => pt.label),
        ...filteredHum.map((pt) => pt.label),
        ...filteredNh3.map((pt) => pt.label),
        ...filteredFtemp.map((pt) => pt.label),
      ])
    ).sort((a, b) => {
      const leftDay = Number(a);
      const rightDay = Number(b);
      if (Number.isFinite(leftDay) && Number.isFinite(rightDay)) return leftDay - rightDay;
      const da = Date.parse(a);
      const db = Date.parse(b);
      if (Number.isNaN(da) || Number.isNaN(db)) return a.localeCompare(b);
      return da - db;
    });

    const buildDatasetFromPoints = (points, { label, borderColor, backgroundColor }) => {
      const byLabel = new Map(points.map((pt) => [pt.label, pt]));
      const data = mergedLabels.map((lbl) => byLabel.get(lbl)?.value ?? null);
      const pointColors = mergedLabels.map((lbl) => statusToColor(byLabel.get(lbl)?.status));
      const metadata = mergedLabels.map((lbl) => {
        const pt = byLabel.get(lbl);
        return pt
          ? {
              target: pt.target,
              day: pt.day ?? null,
              date: pt.date ?? null,
              avgDeviationP: pt.avgDeviationP,
              lowVal: pt.lowVal,
              lowTarget: pt.lowTarget,
              highVal: pt.highVal,
              highTarget: pt.highTarget,
            }
          : {};
      });
      return {
        type: "line",
        label,
        data,
        borderColor,
        backgroundColor,
        pointBackgroundColor: pointColors,
        pointBorderColor: pointColors,
        pointRadius: 4,
        pointHoverRadius: 6,
        fill: false,
        tension: 0.3,
        showLegend: true,
        metadata,
        datalabels: { display: false },
      };
    };

    const datasets = [
      tempGroupEnabled &&
        buildDatasetFromPoints(filteredTemp, {
          label: "Avg Temp",
          borderColor: "#00d1ff",
          backgroundColor: "rgba(0, 209, 255, 0.2)",
        }),
      tempGroupEnabled &&
        buildDatasetFromPoints(filteredHum, {
          label: "Avg Hum",
          borderColor: "#2dce89",
          backgroundColor: "rgba(45, 206, 137, 0.2)",
        }),
      tempGroupEnabled &&
        buildDatasetFromPoints(filteredNh3, {
          label: "Avg NH3",
          borderColor: "#fbcf33",
          backgroundColor: "rgba(251, 207, 51, 0.2)",
        }),
      ftempEnabled &&
        buildDatasetFromPoints(filteredFtemp, {
          label: "Avg FTemp",
          borderColor: "#fb7185",
          backgroundColor: "rgba(251, 113, 133, 0.2)",
        }),
    ].filter(Boolean);

    setEnvTempChartData({
      labels: mergedLabels,
      datasets,
    });
  };

  const buildWaterChartData = (series, threshold, toggles) => {
    if (!series) {
      setWaterChartData(EMPTY_CHART);
      return;
    }
    const { phPoints = [], wtempPoints = [], orpPoints = [] } = series;
    const wtempEnabled = toggles?.["Water Temp"] !== false;
    const phEnabled = toggles?.pH !== false;
    const orpEnabled = toggles?.ORP !== false;
    const filteredPh = phEnabled ? filterByDeviation(phPoints, threshold) : [];
    const filteredWtemp = wtempEnabled ? filterByDeviation(wtempPoints, threshold) : [];
    const filteredOrp = orpEnabled ? filterByDeviation(orpPoints, threshold) : [];

    const mergedLabels = Array.from(
      new Set([
        ...filteredPh.map((pt) => pt.label),
        ...filteredWtemp.map((pt) => pt.label),
        ...filteredOrp.map((pt) => pt.label),
      ])
    ).sort((a, b) => {
      const da = Date.parse(a);
      const db = Date.parse(b);
      if (Number.isNaN(da) || Number.isNaN(db)) return a.localeCompare(b);
      return da - db;
    });

    const buildDatasetFromPoints = (points, { label, borderColor, backgroundColor }) => {
      const byLabel = new Map(points.map((pt) => [pt.label, pt]));
      const data = mergedLabels.map((lbl) => byLabel.get(lbl)?.value ?? null);
      const pointColors = mergedLabels.map((lbl) => statusToColor(byLabel.get(lbl)?.status));
      const metadata = mergedLabels.map((lbl) => {
        const pt = byLabel.get(lbl);
        return pt
          ? {
              target: pt.target,
              avgDeviationP: pt.avgDeviationP,
              lowVal: pt.lowVal,
              lowTarget: pt.lowTarget,
              highVal: pt.highVal,
              highTarget: pt.highTarget,
            }
          : {};
      });
      return {
        type: "line",
        label,
        data,
        borderColor,
        backgroundColor,
        pointBackgroundColor: pointColors,
        pointBorderColor: pointColors,
        pointRadius: 4,
        pointHoverRadius: 6,
        fill: false,
        tension: 0.3,
        showLegend: true,
        metadata,
        datalabels: { display: false },
      };
    };

    const datasets = [
      wtempEnabled &&
        buildDatasetFromPoints(filteredWtemp, {
          label: "Avg Water Temp",
          borderColor: "#00d1ff",
          backgroundColor: "rgba(0, 209, 255, 0.2)",
        }),
      phEnabled &&
        buildDatasetFromPoints(filteredPh, {
          label: "Avg pH",
          borderColor: "#2dce89",
          backgroundColor: "rgba(45, 206, 137, 0.2)",
        }),
      orpEnabled &&
        buildDatasetFromPoints(filteredOrp, {
          label: "Avg ORP",
          borderColor: "#f472b6",
          backgroundColor: "rgba(244, 114, 182, 0.2)",
        }),
    ].filter(Boolean);

    setWaterChartData({ labels: mergedLabels, datasets });
  };

  const buildPerformanceChartData = (series, threshold) => {
    if (!series) {
      setPerformanceChartData(EMPTY_CHART);
      return;
    }
    const limit = Number(threshold);
    const filteredPoints = series.points.filter(
      (pt) => Number.isFinite(pt.deviationp) && pt.deviationp >= limit
    );
    const breakdownLineColor = "#4D93D9";
    const labels = filteredPoints.map((pt) => pt.label);
    const data = filteredPoints.map((pt) => pt.value);
    const mortalityOnlyData = filteredPoints.map((pt) => pt.mortalityOnly);
    const cullsOnlyData = filteredPoints.map((pt) => pt.cullsOnly);
    const pointColors = filteredPoints.map((pt) => statusToColor(pt.status));
    const metadata = filteredPoints.map((pt) => ({
      tooltipType: "mortalityBreakdown",
      day: pt.day ?? null,
      date: pt.date ?? null,
      deviationp: pt.deviationp,
      mortality_rate: pt.mortality_rate,
      increment: pt.increment,
      mortalities_only: pt.mortalityOnly,
      culls_only: pt.cullsOnly,
    }));
    const breakdownMetadata = filteredPoints.map((pt) => ({
      tooltipType: "mortalityBreakdownPart",
      day: pt.day ?? null,
      date: pt.date ?? null,
      total: pt.value,
      mortalities_only: pt.mortalityOnly,
      culls_only: pt.cullsOnly,
    }));

    setPerformanceChartData({
      labels,
      datasets: [
        {
          type: "line",
          label: "Mortality",
          data,
          borderColor: "#D8D8D8",
          backgroundColor: "rgba(216, 216, 216, 0.2)",
          pointBackgroundColor: pointColors,
          pointBorderColor: pointColors,
          pointBorderWidth: 0,
          pointHoverBorderWidth: 0,
          pointRadius: 7,
          pointHoverRadius: 11,
          borderWidth: 3,
          fill: false,
          tension: 0.3,
          showLegend: false,
          metadata,
          datalabels: {
            display: true,
            anchor: "end",
            align: "start",
            offset: -18,
            color: "#fff",
            font: { weight: "bold", size: 13 },
          },
        },
        {
          type: "line",
          label: "Mortality",
          data: mortalityOnlyData,
          borderColor: breakdownLineColor,
          backgroundColor: breakdownLineColor,
          pointBackgroundColor: breakdownLineColor,
          pointBorderColor: breakdownLineColor,
          pointRadius: 2,
          pointHoverRadius: 5,
          borderWidth: 1,
          fill: false,
          tension: 0.3,
          showLegend: true,
          metadata: breakdownMetadata,
          datalabels: { display: false },
        },
        {
          type: "line",
          label: "Culls",
          data: cullsOnlyData,
          borderColor: breakdownLineColor,
          backgroundColor: breakdownLineColor,
          pointBackgroundColor: breakdownLineColor,
          pointBorderColor: breakdownLineColor,
          pointRadius: 2,
          pointHoverRadius: 5,
          borderWidth: 1,
          borderDash: [6, 4],
          fill: false,
          tension: 0.3,
          showLegend: true,
          metadata: breakdownMetadata,
          datalabels: { display: false },
        },
      ],
    });
  };

  const buildPerformanceWeightFcrChartData = (series, threshold) => {
    if (!series) {
      setPerformanceWeightFcrChartData(EMPTY_CHART);
      setPerformanceWeightChartData(EMPTY_CHART);
      setPerformanceFcrChartData(EMPTY_CHART);
      return;
    }
    const limit = Number(threshold);
    const filterPoints = (points) =>
      points.filter((pt) => {
        if (Number.isFinite(pt.deviationp)) return pt.deviationp >= limit;
        return limit <= 0 && Number.isFinite(pt.value);
      });

    const filteredWeight = filterPoints(series.weightPoints);
    const filteredFcr = filterPoints(series.fcrPoints);

    const mergedLabels = Array.from(
      new Set([...filteredWeight.map((pt) => pt.label), ...filteredFcr.map((pt) => pt.label)])
    ).sort((a, b) => {
      const leftDay = Number(a);
      const rightDay = Number(b);
      if (Number.isFinite(leftDay) && Number.isFinite(rightDay)) return leftDay - rightDay;
      const da = Date.parse(a);
      const db = Date.parse(b);
      if (Number.isNaN(da) || Number.isNaN(db)) return a.localeCompare(b);
      return da - db;
    });

    const buildDatasetFromPoints = (
      points,
      {
        label,
        borderColor,
        backgroundColor,
        type,
        yAxisID,
        fill,
        showLine = true,
        order,
        pointRadius,
        pointHoverRadius,
        pointBorderWidth,
        borderDash,
        valueKey = "value",
      }
    ) => {
      const byLabel = new Map(points.map((pt) => [pt.label, pt]));
      const data = mergedLabels.map((lbl) => byLabel.get(lbl)?.[valueKey] ?? null);
      const pointColors = mergedLabels.map((lbl) => statusToColor(byLabel.get(lbl)?.status));
      const metadata = mergedLabels.map((lbl) => {
        const pt = byLabel.get(lbl);
        return pt
          ? {
              target: pt.target,
              deviationp: pt.deviationp,
              day: pt.day,
              date: pt.date ?? null,
            }
          : {};
      });
      return {
        type,
        label,
        data,
        yAxisID,
        borderColor,
        backgroundColor,
        pointBackgroundColor: pointColors,
        pointBorderColor: pointColors,
        pointBorderWidth,
        showLine,
        order,
        borderWidth: type === "bar" ? 1 : 2,
        borderDash,
        barThickness: type === "bar" ? 18 : undefined,
        maxBarThickness: type === "bar" ? 18 : undefined,
        borderRadius: type === "bar" ? 6 : undefined,
        pointRadius: type === "line" ? pointRadius ?? 4 : 0,
        pointHoverRadius: type === "line" ? pointHoverRadius ?? 6 : 0,
        fill: fill ?? false,
        tension: 0.3,
        showLegend: true,
        metadata,
        datalabels:
          type === "bar"
            ? {
                display: true,
                anchor: "end",
                align: "start",
                offset: -18,
                color: "#fff",
                font: { weight: "bold", size: 12 },
              }
            : {
                display: true,
                anchor: "end",
                align: "start",
                offset: -18,
                color: "#fff",
                font: { weight: "bold", size: 12 },
              },
      };
    };

    const weightDataset = buildDatasetFromPoints(filteredWeight, {
      label: "Avg Weight",
      borderColor: "transparent",
      backgroundColor: mergedLabels.map((lbl) => {
        const point = filteredWeight.find((pt) => pt.label === lbl);
        return statusToColor(point?.status);
      }),
      type: "bar",
      yAxisID: "y",
      order: -10,
    });
    weightDataset.datalabels = {
      ...(weightDataset.datalabels || {}),
      formatter: (value) => {
        const numeric = Number(value);
        return Number.isFinite(numeric) ? numeric.toFixed(3) : "";
      },
    };

    const weightTargetDataset = buildDatasetFromPoints(filteredWeight, {
      label: "Weight Target",
      borderColor: "#1a73e8",
      backgroundColor: "rgba(26, 115, 232, 0.12)",
      type: "line",
      yAxisID: "y",
      order: -5,
      pointRadius: 0,
      pointHoverRadius: 0,
      pointBorderWidth: 2,
      valueKey: "target",
    });
    weightTargetDataset.datalabels = { display: false };

    const fcrDataset = buildDatasetFromPoints(filteredFcr, {
      label: "FCR",
      borderColor: "#9e9e9e",
      backgroundColor: "rgba(158, 158, 158, 0.2)",
      type: "line",
      yAxisID: "y1",
      order: 10,
      pointRadius: 6,
      pointHoverRadius: 8,
      pointBorderWidth: 2,
      borderDash: [6, 6],
    });

    setPerformanceWeightFcrChartData({
      labels: mergedLabels,
      datasets: [weightDataset, weightTargetDataset, fcrDataset],
    });
    setPerformanceWeightChartData({
      labels: mergedLabels,
      datasets: [weightDataset, weightTargetDataset],
    });
    setPerformanceFcrChartData({
      labels: mergedLabels,
      datasets: [fcrDataset],
    });
  };

  const buildActivitiesChartData = (payload, filters) => {
    const labels = Array.isArray(payload?.dates) ? payload.dates.map((value) => `${value}`) : [];
    const categories = [
      { key: "vaccination", label: "Vaccination" },
      { key: "sanitation", label: "Sanitation" },
      { key: "feed", label: "Feed" },
      { key: "weights", label: "Weights" },
      { key: "mortality", label: "Mortality" },
    ];

    const datasets = categories.map((category) => {
      const values = Array.isArray(payload?.[category.key]) ? payload[category.key] : [];
      const fallbackKey = category.key.endsWith("s") ? category.key.slice(0, -1) : category.key;
      const statuses = Array.isArray(payload?.[`${category.key}_status`])
        ? payload[`${category.key}_status`]
        : Array.isArray(payload?.[`${fallbackKey}_status`])
        ? payload[`${fallbackKey}_status`]
        : [];
      const messages = Array.isArray(payload?.[`${category.key}_message`])
        ? payload[`${category.key}_message`]
        : Array.isArray(payload?.[`${fallbackKey}_message`])
        ? payload[`${fallbackKey}_message`]
        : [];
      const mergedValues =
        values.length && Array.isArray(values)
          ? values
          : Array.isArray(payload?.[fallbackKey])
          ? payload[fallbackKey]
          : [];

      const matchesStatus = (status) => {
        const normalized = `${status || ""}`.toLowerCase();
        if (normalized === "green") return filters?.Completed !== false;
        if (normalized === "orange") return filters?.["Conditions not met"] !== false;
        if (normalized === "red") return filters?.["Not completed"] !== false;
        return false;
      };

      const data = labels.map((_, idx) => {
        const raw = mergedValues[idx];
        const asString = raw === null || raw === undefined ? "" : String(raw).trim();
        const num = asString && asString.toLowerCase() !== "null" ? Number(asString) : NaN;
        if (!matchesStatus(statuses[idx])) return 0;
        if (Number.isFinite(num)) return num;
        return messages[idx] || statuses[idx] ? 1 : 0;
      });
      const backgroundColor = labels.map((_, idx) => statusToColor(statuses[idx]));
      const metadata = labels.map((_, idx) => ({
        message: messages[idx],
      }));

      return {
        type: "bar",
        label: category.label,
        data,
        backgroundColor,
        borderColor: "transparent",
        stack: "activities",
        stacked: true,
        metadata,
        datalabels: { display: false },
      };
    });

    setActivitiesChartData({ labels, datasets });
  };

  const loadFacilityOptions = useCallback(async (farmId) => {
    const response = await axios.get(broilersByFarmUrl(farmId));
    const rows = Array.isArray(response.data) ? response.data : [];
    return rows
      .map((row) => ({
        id: row.id ?? row.ID ?? row.broilerID ?? row.broilerId ?? row.facilityId,
        name: row.name || row.broilerName || row.facilityName,
        farmId,
      }))
      .filter((row) => row.id != null && row.name);
  }, []);

  const selectReportFacility = useCallback(async (facilityId, version, preferredCycleId) => {
    const id = facilityId != null ? `${facilityId}` : "";
    setSelectedFacilityId(id);
    setCycleOptions([]);
    setSelectedCycleId("");
    if (!id) return;
    try {
      const response = await axios.get(cyclesByFacilityUrl(id));
      if (dropdownRequestVersion.current !== version) return;
      const rows = (Array.isArray(response.data) ? response.data : [])
        .map((row) => ({
          id: row.id ?? row.ID ?? row.cycleId ?? row.cycleID,
          name: row.name || row.cycleName || row.CycleName || row.cycle || row.Cycle,
        }))
        .filter((row) => row.id != null && row.name);
      setCycleOptions(rows);
      const next = rows.find((row) => `${row.id}` === `${preferredCycleId}`) || rows[0];
      setSelectedCycleId(next ? `${next.id}` : "");
    } catch (error) {
      console.warn("Unable to load report cycles", error);
    }
  }, []);

  const selectReportFarm = useCallback(
    async (farmId, version, preferredFacilityId, preferredCycleId, preloaded) => {
      const id = farmId != null ? `${farmId}` : "";
      setSelectedFarmId(id);
      setFacilityOptions([]);
      setSelectedFacilityId("");
      setCycleOptions([]);
      setSelectedCycleId("");
      if (!id) return;
      try {
        const rows = preloaded || (await loadFacilityOptions(id));
        if (dropdownRequestVersion.current !== version) return;
        setFacilityOptions(rows);
        const next = rows.find((row) => `${row.id}` === `${preferredFacilityId}`) || rows[0];
        await selectReportFacility(next?.id, version, preferredCycleId);
      } catch (error) {
        console.warn("Unable to load report facilities", error);
      }
    },
    [loadFacilityOptions, selectReportFacility]
  );

  useEffect(() => {
    const version = ++dropdownRequestVersion.current;
    const initialize = async () => {
      try {
        const session = JSON.parse(window.localStorage.getItem("OmnioLogin"));
        if (!session?.ID) return;
        let stored;
        try {
          stored = JSON.parse(window.localStorage.getItem(FACILITY_SELECTION_KEY));
        } catch {}
        const response = await axios.get(farmsForUserUrl(session.ID));
        if (dropdownRequestVersion.current !== version) return;
        const farms = (Array.isArray(response.data) ? response.data : [])
          .map((row) => ({
            id: row.id ?? row.ID ?? row.farmId ?? row.FarmID ?? row.farmID,
            name: row.name || row.farmName || row.FarmName,
          }))
          .filter((row) => row.id != null && row.name);
        setFarmOptions(farms);
        const requestedFacility = navFacilityId || facilityFromQuery;
        const preferredCycle = cycleFromQuery || stored?.cycleId;
        if (requestedFacility) {
          // Resolve facility-only navigation within the farms available to this user.
          for (const farm of farms) {
            const rows = await loadFacilityOptions(farm.id);
            if (dropdownRequestVersion.current !== version) return;
            if (rows.some((row) => `${row.id}` === `${requestedFacility}`)) {
              await selectReportFarm(farm.id, version, requestedFacility, preferredCycle, rows);
              return;
            }
          }
        }
        const preferredFarm = farmFromQuery || stored?.farmId;
        const farm = farms.find((row) => `${row.id}` === `${preferredFarm}`) || farms[0];
        await selectReportFarm(farm?.id, version, stored?.facilityId, preferredCycle);
      } catch (error) {
        console.warn("Unable to hydrate farm/facility/cycle options for reports", error);
      }
    };
    initialize();
    return () => {
      dropdownRequestVersion.current += 1;
    };
  }, [
    cycleFromQuery,
    facilityFromQuery,
    farmFromQuery,
    navFacilityId,
    loadFacilityOptions,
    selectReportFarm,
  ]);

  const handleFacilityChange = (facilityId) => {
    selectReportFacility(facilityId, ++dropdownRequestVersion.current);
  };

  const handleFarmChange = (farmId) => {
    selectReportFarm(farmId, ++dropdownRequestVersion.current);
  };

  const loadEnvTemp = () => {
    if (!canLoadSelectedReportData()) return;
    const hasFacility =
      selectedFacilityId !== undefined &&
      selectedFacilityId !== null &&
      `${selectedFacilityId}`.length > 0;
    const hasCycle =
      selectedCycleId !== undefined && selectedCycleId !== null && `${selectedCycleId}`.length > 0;
    if (!hasFacility || !hasCycle) {
      setEnvTempChartData(EMPTY_CHART);
      setEnvSeries(null);
      return;
    }
    if (isOffline) {
      applyCachedReportsData(selectedFacilityId, selectedCycleId);
      return;
    }

    let cancelled = false;
    const run = async () => {
      setIsLoadingEnvTemp(true);
      try {
        const facilityId = `${selectedFacilityId}`;
        const cycleId = `${selectedCycleId}`;
        const [tempRes, humRes, nh3Res, ftempRes, phRes, wtempRes, orpRes] = await Promise.all([
          axios.post(tempEnvUrl(facilityId, cycleId), null, createSkipAuthConfig()),
          axios.post(humEnvUrl(facilityId, cycleId), null, createSkipAuthConfig()),
          axios.post(nh3EnvUrl(facilityId, cycleId), null, createSkipAuthConfig()),
          axios.post(floorTempEnvUrl(facilityId, cycleId), null, createSkipAuthConfig()),
          axios.post(phEnvUrl(facilityId, cycleId), null, createSkipAuthConfig()),
          axios.post(waterTempEnvUrl(facilityId, cycleId), null, createSkipAuthConfig()),
          axios.post(orpEnvUrl(facilityId, cycleId), null, createSkipAuthConfig()),
        ]);
        if (cancelled) return;

        const tempRaw = extractRows(tempRes)[0] || {};
        const humRaw = extractRows(humRes)[0] || {};
        const nh3Raw = extractRows(nh3Res)[0] || {};
        const ftempRaw = extractRows(ftempRes)[0] || {};
        const phRaw = extractRows(phRes)[0] || {};
        const wtempRaw = extractRows(wtempRes)[0] || {};
        const orpRaw = extractRows(orpRes)[0] || {};

        const nextEnvSeries = {
          tempPoints: buildEnvSeries(tempRaw, "temp"),
          humPoints: buildEnvSeries(humRaw, "hum"),
          nh3Points: buildEnvSeries(nh3Raw, "nh3"),
          ftempPoints: buildEnvSeries(ftempRaw, "ftemp"),
        };
        const nextWaterSeries = {
          phPoints: buildEnvSeries(phRaw, "ph"),
          wtempPoints: buildEnvSeries(wtempRaw, "wtemp"),
          orpPoints: buildEnvSeries(orpRaw, "orp"),
        };
        setEnvSeries(nextEnvSeries);
        setWaterSeries(nextWaterSeries);
        updateReportsCacheEntry(facilityId, cycleId, {
          envSeries: nextEnvSeries,
          waterSeries: nextWaterSeries,
        });
      } catch (error) {
        console.error("Unable to load environmental temp data", error);
        setEnvTempChartData(EMPTY_CHART);
        setEnvSeries(null);
        setWaterChartData(EMPTY_CHART);
        setWaterSeries(null);
      } finally {
        if (!cancelled) setIsLoadingEnvTemp(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  };

  const loadLighting = () => {
    if (!canLoadSelectedReportData()) return;
    const hasFacility =
      selectedFacilityId !== undefined &&
      selectedFacilityId !== null &&
      `${selectedFacilityId}`.length > 0;
    const hasCycle =
      selectedCycleId !== undefined && selectedCycleId !== null && `${selectedCycleId}`.length > 0;
    if (!hasFacility || !hasCycle) {
      setLightingChartData(EMPTY_CHART);
      setLightingSeries([]);
      setLightingDarkSeries([]);
      return;
    }
    if (isOffline) {
      applyCachedReportsData(selectedFacilityId, selectedCycleId);
      return;
    }

    let cancelled = false;
    const run = async () => {
      try {
        const facilityId = `${selectedFacilityId}`;
        const cycleId = `${selectedCycleId}`;
        const [luxRes, darkRes] = await Promise.all([
          axios.post(luxEnvUrl(facilityId, cycleId), null, createSkipAuthConfig()),
          axios.get(lightingDarkUrl(facilityId, cycleId), createSkipAuthConfig()),
        ]);
        if (cancelled) return;

        const extractRows = (response) =>
          Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response?.data?.data)
            ? response.data.data
            : [];

        const luxRaw = extractRows(luxRes)[0] || {};
        const darkRaw = extractRows(darkRes);
        const nextLightingSeries = buildEnvSeries(luxRaw, "lux");
        const nextLightingDarkSeries = buildLightingDarkSeries(darkRaw);
        setLightingSeries(nextLightingSeries);
        setLightingDarkSeries(nextLightingDarkSeries);
        updateReportsCacheEntry(facilityId, cycleId, {
          lightingSeries: nextLightingSeries,
          lightingDarkSeries: nextLightingDarkSeries,
        });
      } catch (error) {
        console.error("Unable to load lighting data", error);
        setLightingChartData(EMPTY_CHART);
        setLightingSeries([]);
        setLightingDarkSeries([]);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  };

  const loadPerformanceKpi = () => {
    if (!canLoadSelectedReportData()) return;
    const hasFacility =
      selectedFacilityId !== undefined &&
      selectedFacilityId !== null &&
      `${selectedFacilityId}`.length > 0;
    const hasCycle =
      selectedCycleId !== undefined && selectedCycleId !== null && `${selectedCycleId}`.length > 0;
    if (!hasFacility || !hasCycle) {
      setPerformanceChartData(EMPTY_CHART);
      return;
    }
    if (isOffline) {
      applyCachedReportsData(selectedFacilityId, selectedCycleId);
      return;
    }

    let cancelled = false;
    const run = async () => {
      setIsLoadingPerformance(true);
      try {
        const facilityId = `${selectedFacilityId}`;
        const cycleId = `${selectedCycleId}`;
        const response = await axios.post(
          performanceKpiUrl(facilityId, cycleId),
          null,
          createSkipAuthConfig()
        );
        if (cancelled) return;
        const payload = response?.data || {};
        const dates = Array.isArray(payload?.dates)
          ? payload.dates
          : Array.isArray(payload?.timestamp_name)
          ? payload.timestamp_name
          : [];
        const days = Array.isArray(payload?.day) ? payload.day : [];
        const mortality = Array.isArray(payload?.mortality) ? payload.mortality : [];
        const mortalityOnly = Array.isArray(payload?.mortalities_only)
          ? payload.mortalities_only
          : Array.isArray(payload?.mortality_only)
          ? payload.mortality_only
          : [];
        const cullsOnly = Array.isArray(payload?.culls_only) ? payload.culls_only : [];
        const statuses = Array.isArray(payload?.status) ? payload.status : [];
        const deviations = Array.isArray(payload?.deviationp) ? payload.deviationp : [];
        const rates = Array.isArray(payload?.mortality_rate) ? payload.mortality_rate : [];
        const increments = Array.isArray(payload?.increment) ? payload.increment : [];
        const nextPerformanceMeta = {
          live: payload?.live ?? null,
          cumulative: payload?.cumulative ?? null,
          startDate: payload?.start_date ?? "",
          endDate: payload?.end_date ?? "",
        };
        setPerformanceMeta(nextPerformanceMeta);

        const axisValues = days.length ? days : dates;
        const labels = axisValues.map((value, idx) => {
          if (days.length && value !== null && value !== undefined) return String(value);
          return formatDateToDdMmm(dates[idx] ?? value);
        });
        const points = labels
          .map((label, idx) => {
            const value = Number(mortality[idx]);
            if (!label || !Number.isFinite(value)) return null;
            return {
              label,
              day: days[idx] ?? null,
              date: dates[idx] ?? null,
              value,
              status: statuses[idx],
              deviationp: Number(deviations[idx]),
              mortality_rate: Number(rates[idx]),
              increment: Number(increments[idx]),
              mortalityOnly: parseNullableNumber(mortalityOnly[idx]),
              cullsOnly: parseNullableNumber(cullsOnly[idx]),
            };
          })
          .filter(Boolean);

        const nextPerformanceSeries = { points };
        setPerformanceSeries(nextPerformanceSeries);
        updateReportsCacheEntry(facilityId, cycleId, {
          performanceSeries: nextPerformanceSeries,
          performanceMeta: nextPerformanceMeta,
        });
      } catch (error) {
        console.error("Unable to load performance KPI data", error);
        setPerformanceChartData(EMPTY_CHART);
        setPerformanceSeries(null);
        setPerformanceMeta({
          live: null,
          cumulative: null,
          startDate: "",
          endDate: "",
        });
      } finally {
        if (!cancelled) setIsLoadingPerformance(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  };

  const loadPerformanceWeightFcr = () => {
    if (!canLoadSelectedReportData()) return;
    const hasFacility =
      selectedFacilityId !== undefined &&
      selectedFacilityId !== null &&
      `${selectedFacilityId}`.length > 0;
    const hasCycle =
      selectedCycleId !== undefined && selectedCycleId !== null && `${selectedCycleId}`.length > 0;
    if (!hasFacility || !hasCycle) {
      setPerformanceWeightFcrChartData(EMPTY_CHART);
      setPerformanceWeightFcrSeries(null);
      return;
    }
    if (isOffline) {
      applyCachedReportsData(selectedFacilityId, selectedCycleId);
      return;
    }

    let cancelled = false;
    const run = async () => {
      setIsLoadingPerformanceWeightFcr(true);
      try {
        const facilityId = `${selectedFacilityId}`;
        const cycleId = `${selectedCycleId}`;
        const [weightRes, fcrRes] = await Promise.all([
          axios.post(performanceWeightUrl(facilityId, cycleId), null, createSkipAuthConfig()),
          axios.post(performanceFcrUrl(facilityId, cycleId), null, createSkipAuthConfig()),
        ]);
        if (cancelled) return;
        const weightPayload = weightRes?.data || {};
        const fcrPayload = fcrRes?.data || {};

        const weightDates = Array.isArray(weightPayload?.dates) ? weightPayload.dates : [];
        const weightValues = Array.isArray(weightPayload?.weight_average)
          ? weightPayload.weight_average
          : [];
        const weightTargets = Array.isArray(weightPayload?.weight_target)
          ? weightPayload.weight_target
          : [];
        const weightDeviations = Array.isArray(weightPayload?.deviationp)
          ? weightPayload.deviationp
          : [];
        const weightStatuses = Array.isArray(weightPayload?.status) ? weightPayload.status : [];
        const weightDays = Array.isArray(weightPayload?.day) ? weightPayload.day : [];

        const fcrDates = Array.isArray(fcrPayload?.dates) ? fcrPayload.dates : [];
        const fcrValues = Array.isArray(fcrPayload?.fcr) ? fcrPayload.fcr : [];
        const fcrTargets = Array.isArray(fcrPayload?.fcr_target) ? fcrPayload.fcr_target : [];
        const fcrDeviations = Array.isArray(fcrPayload?.deviationp) ? fcrPayload.deviationp : [];
        const fcrStatuses = Array.isArray(fcrPayload?.status) ? fcrPayload.status : [];
        const fcrDays = Array.isArray(fcrPayload?.day) ? fcrPayload.day : [];

        const toNumber = (value) => {
          if (value === null || value === undefined) return null;
          const asString = String(value).trim();
          if (!asString || asString.toLowerCase() === "null") return null;
          const num = Number(asString);
          return Number.isFinite(num) ? num : null;
        };

        const weightAxisValues = weightDays.length ? weightDays : weightDates;
        const weightPoints = weightAxisValues
          .map((axisValue, idx) => {
            const value = toNumber(weightValues[idx]);
            const day = toNumber(weightDays[idx]);
            const label = day !== null ? String(day) : String(weightDates[idx] ?? axisValue ?? "");
            if (!label || !Number.isFinite(value)) return null;
            return {
              label,
              value,
              status: weightStatuses[idx],
              deviationp: toNumber(weightDeviations[idx]),
              target: toNumber(weightTargets[idx]),
              day,
              date: weightDates[idx] ?? null,
            };
          })
          .filter(Boolean);

        const fcrAxisValues = fcrDays.length ? fcrDays : fcrDates;
        const fcrPoints = fcrAxisValues
          .map((axisValue, idx) => {
            const value = toNumber(fcrValues[idx]);
            const day = toNumber(fcrDays[idx]);
            const label = day !== null ? String(day) : String(fcrDates[idx] ?? axisValue ?? "");
            if (!label || !Number.isFinite(value)) return null;
            return {
              label,
              value,
              status: fcrStatuses[idx],
              deviationp: toNumber(fcrDeviations[idx]),
              target: toNumber(fcrTargets[idx]),
              day,
              date: fcrDates[idx] ?? null,
            };
          })
          .filter(Boolean);

        const nextPerformanceWeightFcrSeries = { weightPoints, fcrPoints };
        setPerformanceWeightFcrSeries(nextPerformanceWeightFcrSeries);
        updateReportsCacheEntry(facilityId, cycleId, {
          performanceWeightFcrSeries: nextPerformanceWeightFcrSeries,
        });
      } catch (error) {
        console.error("Unable to load performance weight/FCR data", error);
        setPerformanceWeightFcrChartData(EMPTY_CHART);
        setPerformanceWeightFcrSeries(null);
      } finally {
        if (!cancelled) setIsLoadingPerformanceWeightFcr(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  };

  const loadPerformanceActivities = () => {
    if (!canLoadSelectedReportData()) return;
    const hasFacility =
      selectedFacilityId !== undefined &&
      selectedFacilityId !== null &&
      `${selectedFacilityId}`.length > 0;
    const hasCycle =
      selectedCycleId !== undefined && selectedCycleId !== null && `${selectedCycleId}`.length > 0;
    if (!hasFacility || !hasCycle) {
      setActivitiesChartData(EMPTY_CHART);
      return;
    }
    if (isOffline) {
      applyCachedReportsData(selectedFacilityId, selectedCycleId);
      return;
    }

    let cancelled = false;
    const run = async () => {
      setIsLoadingActivities(true);
      try {
        const facilityId = `${selectedFacilityId}`;
        const cycleId = `${selectedCycleId}`;
        const response = await axios.post(
          performanceActivitiesUrl(facilityId, cycleId),
          null,
          createSkipAuthConfig()
        );
        if (cancelled) return;
        const payload = response?.data || {};
        setActivitiesPayload(payload);
        buildActivitiesChartData(payload, activitiesStatusFilters);
        updateReportsCacheEntry(facilityId, cycleId, {
          activitiesPayload: payload,
        });
      } catch (error) {
        console.error("Unable to load performance activities data", error);
        setActivitiesChartData(EMPTY_CHART);
        setActivitiesPayload(null);
      } finally {
        if (!cancelled) setIsLoadingActivities(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  };

  useEffect(() => {
    buildEnvChartData(envSeries, envMinDeviation, envCheckedItems);
  }, [envSeries, envMinDeviation, envCheckedItems]);

  useEffect(() => {
    buildWaterChartData(waterSeries, waterMinDeviation, waterCheckedItems);
  }, [waterSeries, waterMinDeviation, waterCheckedItems]);

  useEffect(() => {
    buildLightingChartData(
      lightingSeries,
      lightingDarkSeries,
      lightingMinDeviation,
      lightingCheckedItems
    );
  }, [lightingSeries, lightingDarkSeries, lightingMinDeviation, lightingCheckedItems]);

  useEffect(() => {
    buildPerformanceChartData(performanceSeries, performanceMinDeviation);
  }, [performanceSeries, performanceMinDeviation]);

  useEffect(() => {
    buildPerformanceWeightFcrChartData(performanceWeightFcrSeries, performanceMinDeviation);
  }, [performanceWeightFcrSeries, performanceMinDeviation]);

  const loadReportsFeed = useCallback(() => {
    const hasFacility =
      selectedFacilityId !== undefined &&
      selectedFacilityId !== null &&
      `${selectedFacilityId}`.length > 0;
    const hasCycle =
      selectedCycleId !== undefined && selectedCycleId !== null && `${selectedCycleId}`.length > 0;
    if (!canLoadSelectedReportData() || !hasFacility || !hasCycle) {
      setFeedChartData(EMPTY_CHART);
      setFeedTotal(null);
      return;
    }

    let cancelled = false;
    const run = async () => {
      try {
        const response = await axios.post(
          reportsFeedUrl(selectedFacilityId, selectedCycleId),
          null,
          createSkipAuthConfig()
        );
        if (cancelled) return;
        const payload = normalizeFeedReportPayload(response?.data);
        const timestamps = Array.isArray(payload?.timestamp) ? payload.timestamp : [];
        const feedValues = Array.isArray(payload?.feed) ? payload.feed : [];
        const length = Math.max(timestamps.length, feedValues.length);
        const labels = Array.from({ length }, (_, idx) => timestamps[idx] ?? `Entry ${idx + 1}`);
        const data = Array.from({ length }, (_, idx) => {
          const value = feedValues[idx];
          const numeric = Number(value);
          return Number.isFinite(numeric) ? numeric : null;
        });

        setFeedChartData({
          labels,
          datasets: [
            {
              label: "Feed Consumption (kg)",
              data,
              backgroundColor: "#c3971d",
            },
          ],
        });
        const total = Number(payload?.feed_total);
        setFeedTotal(Number.isFinite(total) ? total : null);
      } catch (error) {
        console.error("Unable to load feed consumption data", error);
        setFeedChartData(EMPTY_CHART);
        setFeedTotal(null);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [canLoadSelectedReportData, selectedFacilityId, selectedCycleId]);

  useEffect(() => loadReportsFeed(), [loadReportsFeed]);

  const loadTopIssuesEnvironment = () => {
    if (!canLoadSelectedReportData()) return;
    const hasFacility =
      selectedFacilityId !== undefined &&
      selectedFacilityId !== null &&
      `${selectedFacilityId}`.length > 0;
    const hasCycle =
      selectedCycleId !== undefined && selectedCycleId !== null && `${selectedCycleId}`.length > 0;
    if (!hasFacility || !hasCycle) {
      setTopIssuesEnvironmentChartData(EMPTY_CHART);
      return;
    }
    if (isOffline) {
      applyCachedReportsData(selectedFacilityId, selectedCycleId);
      return;
    }

    let cancelled = false;
    const run = async () => {
      try {
        const facilityId = `${selectedFacilityId}`;
        const cycleId = `${selectedCycleId}`;
        const response = await axios.post(
          reportsTopIssuesEnvironmentUrl(facilityId, cycleId),
          null,
          createSkipAuthConfig()
        );
        if (cancelled) return;
        const rawPayload = Array.isArray(response?.data) ? response.data : [];
        let rows = [];
        if (rawPayload.length && Array.isArray(rawPayload[0]?.recordDate)) {
          const grouped = rawPayload[0] || {};
          const recordDates = Array.isArray(grouped.recordDate) ? grouped.recordDate : [];
          const cycleDays = Array.isArray(grouped.broiler_cycle_day)
            ? grouped.broiler_cycle_day
            : Array.isArray(grouped.day)
            ? grouped.day
            : [];
          rows = recordDates.map((recordDate, idx) => ({
            parametre: Array.isArray(grouped.parametre) ? grouped.parametre[idx] : null,
            recordDate,
            broilerCycleDay: cycleDays[idx] ?? null,
            avgDeviationP: Array.isArray(grouped.avgDeviationP) ? grouped.avgDeviationP[idx] : null,
            targetL: Array.isArray(grouped.targetL) ? grouped.targetL[idx] : null,
            targetH: Array.isArray(grouped.targetH) ? grouped.targetH[idx] : null,
            lowVal: Array.isArray(grouped.lowVal) ? grouped.lowVal[idx] : null,
            highVal: Array.isArray(grouped.highVal) ? grouped.highVal[idx] : null,
            avgVal: Array.isArray(grouped.avgVal) ? grouped.avgVal[idx] : null,
            status: Array.isArray(grouped.status) ? grouped.status[idx] : null,
          }));
        } else {
          rows = rawPayload;
        }

        const payload = [...rows]
          .sort((a, b) => {
            const leftDayValue = a?.broilerCycleDay ?? a?.broiler_cycle_day ?? a?.day;
            const rightDayValue = b?.broilerCycleDay ?? b?.broiler_cycle_day ?? b?.day;
            const leftDay = Number(leftDayValue);
            const rightDay = Number(rightDayValue);
            if (
              leftDayValue !== null &&
              leftDayValue !== undefined &&
              rightDayValue !== null &&
              rightDayValue !== undefined &&
              Number.isFinite(leftDay) &&
              Number.isFinite(rightDay)
            ) {
              return leftDay - rightDay;
            }
            const leftKey = getDateSortKey(a?.recordDate);
            const rightKey = getDateSortKey(b?.recordDate);

            if (leftKey && rightKey) {
              return leftKey.localeCompare(rightKey);
            }

            return `${a?.recordDate ?? ""}`.localeCompare(`${b?.recordDate ?? ""}`);
          })
          .slice(0, 10);
        const labels = payload.map((item, idx) => {
          const cycleDay = item?.broilerCycleDay ?? item?.broiler_cycle_day ?? item?.day;
          return cycleDay !== null && cycleDay !== undefined
            ? String(cycleDay)
            : item?.recordDate ?? `Entry ${idx + 1}`;
        });
        const data = payload.map((item) => {
          const numeric = Number(item?.avgVal);
          return Number.isFinite(numeric) ? numeric : null;
        });
        const metadata = payload.map((item) => ({
          day: item?.broilerCycleDay ?? item?.broiler_cycle_day ?? item?.day ?? null,
          date: item?.recordDate ?? null,
          parametre: item?.parametre ?? null,
          avgDeviationP: item?.avgDeviationP ?? null,
          avgVal: item?.avgVal ?? null,
          targetL: item?.targetL ?? null,
          targetH: item?.targetH ?? null,
          lowVal: item?.lowVal ?? null,
          highVal: item?.highVal ?? null,
        }));
        const statusColors = payload.map((item) => {
          const status = String(item?.status ?? "")
            .trim()
            .toLowerCase();
          if (status === "green") return "#46c44c";
          if (status === "orange" || status === "amber" || status === "yellow") return "#f39507";
          if (status === "red") return "#d32f2f";
          return "#9e9e9e";
        });
        const parameterLabel =
          payload.find((item) => item?.parametre != null)?.parametre ?? "Parameter";
        setTopIssuesEnvironmentChartData({
          labels,
          datasets: [
            {
              label: parameterLabel,
              data,
              metadata,
              backgroundColor: statusColors,
              datalabels: {
                display: true,
                anchor: "end",
                align: "top",
                rotation: -45,
                color: "#ffffff",
                offset: 0,
                formatter: (_value, context) => {
                  const param = context?.dataset?.metadata?.[context?.dataIndex]?.parametre;
                  return param ?? "";
                },
              },
            },
          ],
        });
        updateReportsCacheEntry(facilityId, cycleId, {
          topIssuesEnvironmentChartData: {
            labels,
            datasets: [
              {
                label: parameterLabel,
                data,
                metadata,
                backgroundColor: statusColors,
                datalabels: {
                  display: true,
                  anchor: "end",
                  align: "top",
                  rotation: -45,
                  color: "#ffffff",
                  offset: 0,
                },
              },
            ],
          },
        });
      } catch (error) {
        console.error("Unable to load Top 10 Issues Environment data", error);
        setTopIssuesEnvironmentChartData(EMPTY_CHART);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  };

  const loadTopIssuesPerformance = () => {
    if (!canLoadSelectedReportData()) return;
    const hasFacility =
      selectedFacilityId !== undefined &&
      selectedFacilityId !== null &&
      `${selectedFacilityId}`.length > 0;
    const hasCycle =
      selectedCycleId !== undefined && selectedCycleId !== null && `${selectedCycleId}`.length > 0;
    if (!hasFacility || !hasCycle) {
      setTopIssuesPerformanceChartData(EMPTY_CHART);
      return;
    }
    if (isOffline) {
      applyCachedReportsData(selectedFacilityId, selectedCycleId);
      return;
    }

    let cancelled = false;
    const run = async () => {
      try {
        const facilityId = `${selectedFacilityId}`;
        const cycleId = `${selectedCycleId}`;
        const response = await axios.post(
          reportsTopIssuesPerformanceUrl(facilityId, cycleId),
          null,
          createSkipAuthConfig()
        );
        if (cancelled) return;
        const rows = Array.isArray(response?.data) ? response.data : [];
        const toNumber = (value) => {
          if (value === null || value === undefined) return null;
          const asString = String(value).trim();
          if (!asString || asString.toLowerCase() === "null") return null;
          const numeric = Number(asString);
          return Number.isFinite(numeric) ? numeric : null;
        };
        const flattenKpiSeries = (items, type) =>
          (Array.isArray(items) ? items : [])
            .flatMap((entry) => (Array.isArray(entry?.body) ? entry.body : []))
            .map((bodyEntry) => {
              const isMortality = type === "mortality";
              const isWeight = type === "weight";
              const isFcr = type === "fcr";
              const yValue = isMortality
                ? toNumber(bodyEntry?.mortality)
                : isWeight
                ? toNumber(bodyEntry?.weight_average)
                : toNumber(bodyEntry?.fcr);
              if (!Number.isFinite(yValue)) return null;
              const targetLow = toNumber(bodyEntry?.mortality_targetL);
              const targetHigh = toNumber(bodyEntry?.mortality_targetH);
              const cycleDay = toNumber(
                bodyEntry?.broiler_cycle_day ?? bodyEntry?.weight_day ?? bodyEntry?.fcr_day
              );
              return {
                labelDay:
                  cycleDay !== null
                    ? String(cycleDay)
                    : bodyEntry?.date
                    ? formatDateToDdMmm(bodyEntry.date)
                    : "",
                sortDate: bodyEntry?.date ?? "",
                labelKey: isFcr ? "FCR" : isWeight ? "Weight" : "Mortality",
                hasKpiMortality: isMortality,
                hasKpiWeight: isWeight,
                hasKpiFcr: isFcr,
                yValue,
                deviationp: isMortality
                  ? toNumber(bodyEntry?.mortality_dp)
                  : isWeight
                  ? toNumber(bodyEntry?.weight_dp)
                  : toNumber(bodyEntry?.fcr_dp),
                mortality_today: toNumber(bodyEntry?.mortality),
                mortality_rate: toNumber(bodyEntry?.mortality_rate),
                target_green:
                  targetLow !== null || targetHigh !== null
                    ? `${formatSummaryValue(targetLow)} - ${formatSummaryValue(targetHigh)}`
                    : null,
                day: cycleDay,
                weight_average: toNumber(bodyEntry?.weight_average),
                weight_target: toNumber(bodyEntry?.weight_target),
                fcr: toNumber(bodyEntry?.fcr),
                fcr_target: toNumber(bodyEntry?.fcr_target),
                status:
                  bodyEntry?.mortality_status ??
                  bodyEntry?.weight_status ??
                  bodyEntry?.fcr_status ??
                  bodyEntry?.status,
              };
            })
            .filter(Boolean);

        const normalized = rows
          .flatMap((item) => [
            ...flattenKpiSeries(item?.kpi_mortality, "mortality"),
            ...flattenKpiSeries(item?.kpi_weight, "weight"),
            ...flattenKpiSeries(item?.kpi_fcr, "fcr"),
          ])
          .sort((left, right) => {
            const leftDay = Number(left?.day);
            const rightDay = Number(right?.day);
            if (
              left?.day !== null &&
              right?.day !== null &&
              Number.isFinite(leftDay) &&
              Number.isFinite(rightDay)
            ) {
              return leftDay - rightDay;
            }
            const leftDate = Date.parse(left?.sortDate);
            const rightDate = Date.parse(right?.sortDate);
            if (Number.isFinite(leftDate) && Number.isFinite(rightDate)) {
              return leftDate - rightDate;
            }
            return String(left?.sortDate ?? "").localeCompare(String(right?.sortDate ?? ""));
          })
          .slice(0, 10);

        const labels = normalized.map((item) => item.labelDay);
        const data = normalized.map((item) => {
          const numeric = Number(item?.yValue);
          return Number.isFinite(numeric) ? numeric : null;
        });
        const metadata = normalized.map((item) => ({
          date: item?.sortDate ?? null,
          deviationp: item?.deviationp ?? null,
          mortality_today: item?.mortality_today ?? null,
          mortality_rate: item?.mortality_rate ?? null,
          target_green: item?.target_green ?? null,
          hasKpiMortality: item?.hasKpiMortality ?? false,
          hasKpiWeight: item?.hasKpiWeight ?? false,
          hasKpiFcr: item?.hasKpiFcr ?? false,
          day: item?.day ?? null,
          weight_average: item?.weight_average ?? null,
          weight_target: item?.weight_target ?? null,
          fcr: item?.fcr ?? null,
          fcr_target: item?.fcr_target ?? null,
        }));
        const statusColors = normalized.map((item) => {
          const status = String(item?.status ?? "")
            .trim()
            .toLowerCase();
          if (status === "green") return "#46c44c";
          if (status === "orange" || status === "amber" || status === "yellow") return "#f39507";
          if (status === "red") return "#d32f2f";
          return "#9e9e9e";
        });
        const seriesLabel = normalized.find((item) => item?.labelKey)?.labelKey ?? "Performance";

        const nextChart = {
          labels,
          datasets: [
            {
              label: seriesLabel,
              data,
              metadata,
              backgroundColor: statusColors,
              datalabels: {
                display: true,
                anchor: "end",
                align: "top",
                rotation: -45,
                formatter: (_value, context) =>
                  context?.dataset?.metadata?.[context?.dataIndex]?.hasKpiFcr
                    ? "FCR"
                    : context?.dataset?.metadata?.[context?.dataIndex]?.hasKpiWeight
                    ? "Weight"
                    : context?.dataset?.metadata?.[context?.dataIndex]?.hasKpiMortality
                    ? "Mortality"
                    : "",
              },
            },
          ],
        };
        setTopIssuesPerformanceChartData(nextChart);
        updateReportsCacheEntry(facilityId, cycleId, {
          topIssuesPerformanceChartData: nextChart,
        });
      } catch (error) {
        console.error("Unable to load Top 10 Issues Performance data", error);
        setTopIssuesPerformanceChartData(EMPTY_CHART);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  };

  useEffect(() => {
    const hasFacility =
      selectedFacilityId !== undefined &&
      selectedFacilityId !== null &&
      `${selectedFacilityId}`.length > 0;
    const hasCycle =
      selectedCycleId !== undefined && selectedCycleId !== null && `${selectedCycleId}`.length > 0;
    if (!canLoadSelectedReportData() || !hasFacility || !hasCycle) {
      setWaterConsumptionChartData(EMPTY_CHART);
      setWaterConsumptionTotal(null);
      return;
    }

    let cancelled = false;
    const run = async () => {
      try {
        const response = await axios.post(
          reportsWaterUrl(selectedFacilityId, selectedCycleId),
          null,
          createSkipAuthConfig()
        );
        if (cancelled) return;
        const payload = response?.data || {};
        const timestamps = Array.isArray(payload?.timestamp) ? payload.timestamp : [];
        const waterUsage = Array.isArray(payload?.water_consump) ? payload.water_consump : [];
        const waterAvgBird = Array.isArray(payload?.water_avgbird) ? payload.water_avgbird : [];
        const waterIncrement = Array.isArray(payload?.water_increment)
          ? payload.water_increment
          : [];
        const length = Math.max(timestamps.length, waterUsage.length);
        const labels = Array.from({ length }, (_, idx) => timestamps[idx] ?? `Entry ${idx + 1}`);
        const data = Array.from({ length }, (_, idx) => {
          const value = waterUsage[idx];
          const numeric = Number(value);
          return Number.isFinite(numeric) ? numeric : null;
        });
        const metadata = Array.from({ length }, (_, idx) => ({
          water_bird: waterAvgBird[idx] ?? null,
          water_increment: waterIncrement[idx] ?? null,
        }));

        setWaterConsumptionChartData({
          labels,
          datasets: [
            {
              label: "Water Consumption",
              data,
              metadata,
              showLegend: false,
              spanGaps: false,
              backgroundColor: "#153E64",
              borderColor: "#D8D8D8",
              tension: 0.4,
              pointRadius: 6,
              pointHoverRadius: 8,
              borderWidth: 1,
              fill: false,
              datalabels: { display: false },
            },
          ],
        });

        const total = Number(payload?.water_total);
        setWaterConsumptionTotal(Number.isFinite(total) ? total : null);
      } catch (error) {
        console.error("Unable to load water consumption data", error);
        setWaterConsumptionChartData(EMPTY_CHART);
        setWaterConsumptionTotal(null);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [canLoadSelectedReportData, selectedFacilityId, selectedCycleId]);

  const loadSummary = () => {
    if (!canLoadSelectedReportData()) return;
    const hasFacility =
      selectedFacilityId !== undefined &&
      selectedFacilityId !== null &&
      `${selectedFacilityId}`.length > 0;
    const hasCycle =
      selectedCycleId !== undefined && selectedCycleId !== null && `${selectedCycleId}`.length > 0;
    if (!hasFacility || !hasCycle) {
      setSummaryRows([]);
      setIsLoadingSummary(false);
      return;
    }
    if (isOffline) {
      applyCachedReportsData(selectedFacilityId, selectedCycleId);
      return;
    }

    let cancelled = false;
    const run = async () => {
      setIsLoadingSummary(true);
      try {
        const response = await axios.get(
          reportsSummaryUrl(selectedFacilityId, selectedCycleId),
          createSkipAuthConfig()
        );
        if (cancelled) return;
        const payload = response?.data;
        const nextRows = Array.isArray(payload)
          ? payload.map((row) => ({
              ...row,
              day: pickFirst(row?.day, row?.Day, row?.cycleDay, row?.CycleDay) ?? null,
            }))
          : [];
        setSummaryRows(nextRows);
        updateReportsCacheEntry(selectedFacilityId, selectedCycleId, {
          summaryRows: nextRows,
        });
      } catch (error) {
        console.error("Unable to load summary data", error);
        if (!cancelled) setSummaryRows([]);
      } finally {
        if (!cancelled) setIsLoadingSummary(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  };

  const formatSummaryValue = (value, decimals = null) => {
    if (value === null || value === undefined || value === "") return "--";
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return decimals !== null ? numeric.toFixed(decimals) : numeric;
    }
    return String(value);
  };

  const parseNullableNumber = (value) => {
    if (value === null || value === undefined) return null;
    const asString = String(value).trim();
    if (!asString || asString.toLowerCase() === "null") return null;
    const num = Number(asString);
    return Number.isFinite(num) ? num : null;
  };

  const normalizePerformanceDateKey = (value) => {
    if (!value) return "";
    const raw = String(value).trim();
    const dateOnly = raw.split("T")[0];
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateOnly)) {
      const [day, month, year] = dateOnly.split("-");
      return `${year}-${month}-${day}`;
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) return dateOnly;
    const parsed = new Date(dateOnly);
    if (Number.isNaN(parsed.getTime())) return dateOnly;
    return parsed.toISOString().split("T")[0];
  };

  const parsePerformanceTableRows = (payload) => {
    const entries = Array.isArray(payload) ? payload : payload ? [payload] : [];
    const rowsByDate = new Map();
    const ensureRow = (dateValue, dayValue = null) => {
      const key = normalizePerformanceDateKey(dateValue);
      if (!key) return null;
      if (!rowsByDate.has(key)) rowsByDate.set(key, { recordDate: dateValue, day: dayValue });
      else if (rowsByDate.get(key).day == null && dayValue != null) {
        rowsByDate.get(key).day = dayValue;
      }
      return { key, row: rowsByDate.get(key) };
    };

    entries.forEach((entry) => {
      const mortalityKpis = Array.isArray(entry?.kpi_mortality) ? entry.kpi_mortality : [];
      const weightKpis = Array.isArray(entry?.kpi_weight) ? entry.kpi_weight : [];
      const fcrKpis = Array.isArray(entry?.kpi_fcr) ? entry.kpi_fcr : [];
      const facilityDate = entry?.facility_info?.date ?? entry?.date ?? null;
      const facilityDay =
        entry?.facility_info?.broiler_cycle_day ?? entry?.broiler_cycle_day ?? entry?.day ?? null;

      if (mortalityKpis.length || weightKpis.length || fcrKpis.length) {
        mortalityKpis.forEach((kpi) => {
          const dateValue = kpi?.date ?? facilityDate;
          const target = ensureRow(dateValue, facilityDay);
          if (!target) return;
          const targetLow = parseNullableNumber(kpi?.mortality_targetL);
          const targetHigh = parseNullableNumber(kpi?.mortality_targetH);
          Object.assign(target.row, {
            recordDate: target.row.recordDate || dateValue,
            mortality: parseNullableNumber(kpi?.mortality),
            mortalityRate: parseNullableNumber(kpi?.mortality_rate),
            mortalityCumul: parseNullableNumber(kpi?.mortality_increment),
            mortalityDeviation: parseNullableNumber(kpi?.mortality_dp),
            mortalityTarget:
              targetLow !== null || targetHigh !== null
                ? `${formatSummaryValue(targetLow)} - ${formatSummaryValue(targetHigh)}`
                : null,
          });
        });

        weightKpis.forEach((kpi) => {
          const dateValue = kpi?.date ?? facilityDate;
          const target = ensureRow(dateValue, facilityDay);
          if (!target) return;
          Object.assign(target.row, {
            recordDate: target.row.recordDate || dateValue,
            weightDay: parseNullableNumber(kpi?.weight_day),
            weightAvg: parseNullableNumber(kpi?.weight_average),
            weightTarget: parseNullableNumber(kpi?.weight_target),
            weightDeviation: parseNullableNumber(kpi?.weight_dp),
          });
        });

        fcrKpis.forEach((kpi) => {
          const dateValue = kpi?.date ?? facilityDate;
          const target = ensureRow(dateValue, facilityDay);
          if (!target) return;
          Object.assign(target.row, {
            recordDate: target.row.recordDate || dateValue,
            fcrValue: parseNullableNumber(kpi?.fcr),
            fcrTarget: parseNullableNumber(kpi?.fcr_target),
            fcrDeviation: parseNullableNumber(kpi?.fcr_dp),
          });
        });

        return;
      }

      const mortalityDates = Array.isArray(entry?.mortality_date) ? entry.mortality_date : [];
      const mortalityRates = Array.isArray(entry?.mortality_rate) ? entry.mortality_rate : [];
      const mortalityValues = Array.isArray(entry?.mortality) ? entry.mortality : [];
      const mortalityIncrement = Array.isArray(entry?.mortality_increment)
        ? entry.mortality_increment
        : [];
      const mortalityDp = Array.isArray(entry?.mortality_dp) ? entry.mortality_dp : [];

      const weightDates = Array.isArray(entry?.weight_date) ? entry.weight_date : [];
      const weightAverage = Array.isArray(entry?.weight_average) ? entry.weight_average : [];
      const weightTarget = Array.isArray(entry?.weight_target) ? entry.weight_target : [];
      const weightDp = Array.isArray(entry?.weight_dp) ? entry.weight_dp : [];
      const weightDay = Array.isArray(entry?.weight_day) ? entry.weight_day : [];

      const fcrDates = Array.isArray(entry?.fcr_date) ? entry.fcr_date : [];
      const fcrValues = Array.isArray(entry?.fcr) ? entry.fcr : [];
      const fcrTarget = Array.isArray(entry?.fcr_target) ? entry.fcr_target : [];
      const fcrDp = Array.isArray(entry?.fcr_dp) ? entry.fcr_dp : [];

      mortalityDates.forEach((dateValue, idx) => {
        const target = ensureRow(dateValue, facilityDay);
        if (!target) return;
        Object.assign(target.row, {
          recordDate: target.row.recordDate || dateValue,
          mortality: parseNullableNumber(mortalityValues[idx]),
          mortalityRate: parseNullableNumber(mortalityRates[idx]),
          mortalityCumul: parseNullableNumber(mortalityIncrement[idx]),
          mortalityDeviation: parseNullableNumber(mortalityDp[idx]),
        });
      });

      weightDates.forEach((dateValue, idx) => {
        const target = ensureRow(dateValue, facilityDay);
        if (!target) return;
        Object.assign(target.row, {
          recordDate: target.row.recordDate || dateValue,
          weightDay: parseNullableNumber(weightDay[idx]),
          weightAvg: parseNullableNumber(weightAverage[idx]),
          weightTarget: parseNullableNumber(weightTarget[idx]),
          weightDeviation: parseNullableNumber(weightDp[idx]),
        });
      });

      fcrDates.forEach((dateValue, idx) => {
        const target = ensureRow(dateValue, facilityDay);
        if (!target) return;
        Object.assign(target.row, {
          recordDate: target.row.recordDate || dateValue,
          fcrValue: parseNullableNumber(fcrValues[idx]),
          fcrTarget: parseNullableNumber(fcrTarget[idx]),
          fcrDeviation: parseNullableNumber(fcrDp[idx]),
        });
      });
    });

    return Array.from(rowsByDate.entries())
      .sort(([left], [right]) => {
        const leftDate = Date.parse(left);
        const rightDate = Date.parse(right);
        if (Number.isNaN(leftDate) || Number.isNaN(rightDate)) return left.localeCompare(right);
        return leftDate - rightDate;
      })
      .map(([, row]) => row);
  };

  const summaryColumns = [
    { key: "recordDate", label: "Date", align: "center", sortable: true },
    { key: "day", label: "Day", align: "center", sortable: true },
    { key: "parametre", label: "Parametre", align: "center", sortable: true },
    { key: "avgDeviationP", label: "Avg Deviation", align: "center", decimals: 1, sortable: true },
    { key: "lowVal", label: "Low", align: "center", decimals: 1, sortable: false },
    { key: "highVal", label: "High", align: "center", decimals: 1, sortable: false },
    { key: "avgVal", label: "Avg", align: "center", decimals: 1, sortable: false },
    { key: "targetL", label: "Low Target", align: "center", decimals: 1, sortable: false },
    { key: "targetH", label: "High Target", align: "center", decimals: 1, sortable: false },
    { key: "sensor", label: "Sensor", align: "center", sortable: false },
  ];

  const loadPerformanceTable = () => {
    if (!canLoadSelectedReportData()) return;
    const hasFacility =
      selectedFacilityId !== undefined &&
      selectedFacilityId !== null &&
      `${selectedFacilityId}`.length > 0;
    const hasCycle =
      selectedCycleId !== undefined && selectedCycleId !== null && `${selectedCycleId}`.length > 0;
    if (!hasFacility || !hasCycle) {
      setPerformanceTableRows([]);
      setIsLoadingPerformanceTable(false);
      return;
    }
    if (isOffline) {
      applyCachedReportsData(selectedFacilityId, selectedCycleId);
      return;
    }

    let cancelled = false;
    const run = async () => {
      setIsLoadingPerformanceTable(true);
      try {
        const response = await axios.get(
          reportsPerformanceUrl(selectedFacilityId, selectedCycleId),
          createSkipAuthConfig()
        );
        if (cancelled) return;
        const nextRows = parsePerformanceTableRows(response?.data);
        setPerformanceTableRows(nextRows);
        updateReportsCacheEntry(selectedFacilityId, selectedCycleId, {
          performanceTableRows: nextRows,
        });
      } catch (error) {
        console.error("Unable to load performance table data", error);
        if (!cancelled) setPerformanceTableRows([]);
      } finally {
        if (!cancelled) setIsLoadingPerformanceTable(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  };

  const applyCachedMinDeviationDefaults = useCallback((facilityId, cycleId) => {
    const cached = readReportsCacheEntry(facilityId, cycleId);
    if (!cached) return false;
    if (cached.envMinDeviationDefaultEnabled) {
      setEnvMinDeviationDefaultEnabled(true);
      setEnvMinDeviation(
        normalizeMinDeviation(cached.envMinDeviationDefault ?? cached.envMinDeviation ?? 0)
      );
    } else {
      setEnvMinDeviationDefaultEnabled(false);
      setEnvMinDeviation(0);
    }
    if (cached.waterMinDeviationDefaultEnabled) {
      setWaterMinDeviationDefaultEnabled(true);
      setWaterMinDeviation(
        normalizeMinDeviation(cached.waterMinDeviationDefault ?? cached.waterMinDeviation ?? 0)
      );
    } else {
      setWaterMinDeviationDefaultEnabled(false);
      setWaterMinDeviation(0);
    }
    if (cached.lightingMinDeviationDefaultEnabled) {
      setLightingMinDeviationDefaultEnabled(true);
      setLightingMinDeviation(
        normalizeMinDeviation(
          cached.lightingMinDeviationDefault ?? cached.lightingMinDeviation ?? 0
        )
      );
    } else {
      setLightingMinDeviationDefaultEnabled(false);
      setLightingMinDeviation(0);
    }
    if (cached.performanceMinDeviationDefaultEnabled) {
      setPerformanceMinDeviationDefaultEnabled(true);
      setPerformanceMinDeviation(
        normalizeMinDeviation(
          cached.performanceMinDeviationDefault ?? cached.performanceMinDeviation ?? 0
        )
      );
    } else {
      setPerformanceMinDeviationDefaultEnabled(false);
      setPerformanceMinDeviation(0);
    }
    if (cached.summaryMinDeviationDefaultEnabled) {
      setSummaryMinDeviationDefaultEnabled(true);
      setSummaryMinDeviation(
        normalizeMinDeviation(cached.summaryMinDeviationDefault ?? cached.summaryMinDeviation ?? 0)
      );
    } else {
      setSummaryMinDeviationDefaultEnabled(false);
      setSummaryMinDeviation(0);
    }
    return true;
  }, []);

  const applyCachedReportsData = useCallback(
    (facilityId, cycleId) => {
      const cached = readReportsCacheEntry(facilityId, cycleId);
      if (!cached) return false;
      if (cached.envSeries) setEnvSeries(cached.envSeries);
      if (cached.waterSeries) setWaterSeries(cached.waterSeries);
      if (Array.isArray(cached.lightingSeries)) setLightingSeries(cached.lightingSeries);
      else if (Array.isArray(cached.envSeries?.luxPoints))
        setLightingSeries(cached.envSeries.luxPoints);
      if (Array.isArray(cached.lightingDarkSeries))
        setLightingDarkSeries(cached.lightingDarkSeries);
      else setLightingDarkSeries([]);
      if (cached.performanceSeries) setPerformanceSeries(cached.performanceSeries);
      if (cached.performanceWeightFcrSeries) {
        setPerformanceWeightFcrSeries(cached.performanceWeightFcrSeries);
      }
      if (cached.activitiesPayload) setActivitiesPayload(cached.activitiesPayload);
      if (cached.performanceMeta) setPerformanceMeta(cached.performanceMeta);
      if (cached.lightingChartData && !Array.isArray(cached.lightingSeries)) {
        setLightingChartData(cached.lightingChartData);
      }
      if (cached.topIssuesEnvironmentChartData) {
        setTopIssuesEnvironmentChartData(cached.topIssuesEnvironmentChartData);
      }
      if (cached.topIssuesPerformanceChartData) {
        setTopIssuesPerformanceChartData(cached.topIssuesPerformanceChartData);
      }
      if (Array.isArray(cached.performanceTableRows)) {
        setPerformanceTableRows(cached.performanceTableRows);
      }
      if (Array.isArray(cached.summaryRows)) setSummaryRows(cached.summaryRows);
      applyCachedMinDeviationDefaults(facilityId, cycleId);
      if (cached.envCheckedItems) setEnvCheckedItems(cached.envCheckedItems);
      if (cached.waterCheckedItems) setWaterCheckedItems(cached.waterCheckedItems);
      if (cached.lightingCheckedItems) setLightingCheckedItems(cached.lightingCheckedItems);
      if (cached.activitiesStatusFilters)
        setActivitiesStatusFilters(cached.activitiesStatusFilters);
      setIsLoadingSummary(false);
      setIsLoadingPerformanceTable(false);
      return true;
    },
    [applyCachedMinDeviationDefaults]
  );

  useEffect(() => {
    if (!selectedFacilityId || !selectedCycleId || !isOffline) return;
    applyCachedReportsData(selectedFacilityId, selectedCycleId);
  }, [applyCachedReportsData, isOffline, selectedCycleId, selectedFacilityId]);

  useEffect(() => {
    if (!selectedFacilityId || !selectedCycleId) return;
    applyCachedMinDeviationDefaults(selectedFacilityId, selectedCycleId);
  }, [applyCachedMinDeviationDefaults, selectedCycleId, selectedFacilityId]);

  useEffect(() => {
    if (!selectedFacilityId || !selectedCycleId) return;
    updateReportsCacheEntry(selectedFacilityId, selectedCycleId, {
      envMinDeviationDefaultEnabled,
      envMinDeviationDefault: envMinDeviationDefaultEnabled ? envMinDeviation : 0,
      envCheckedItems,
      waterMinDeviationDefaultEnabled,
      waterMinDeviationDefault: waterMinDeviationDefaultEnabled ? waterMinDeviation : 0,
      waterCheckedItems,
      lightingMinDeviationDefaultEnabled,
      lightingMinDeviationDefault: lightingMinDeviationDefaultEnabled ? lightingMinDeviation : 0,
      lightingCheckedItems,
      performanceMinDeviationDefaultEnabled,
      performanceMinDeviationDefault: performanceMinDeviationDefaultEnabled
        ? performanceMinDeviation
        : 0,
      summaryMinDeviationDefaultEnabled,
      summaryMinDeviationDefault: summaryMinDeviationDefaultEnabled ? summaryMinDeviation : 0,
      activitiesStatusFilters,
    });
  }, [
    activitiesStatusFilters,
    envCheckedItems,
    envMinDeviation,
    envMinDeviationDefaultEnabled,
    lightingCheckedItems,
    lightingMinDeviation,
    lightingMinDeviationDefaultEnabled,
    performanceMinDeviation,
    performanceMinDeviationDefaultEnabled,
    summaryMinDeviation,
    summaryMinDeviationDefaultEnabled,
    selectedCycleId,
    selectedFacilityId,
    waterCheckedItems,
    waterMinDeviation,
    waterMinDeviationDefaultEnabled,
  ]);

  const sortedSummaryRows = useMemo(() => {
    if (!summaryRows.length) return [];
    const compare = (a, b) => {
      const left = a?.[summaryOrderBy];
      const right = b?.[summaryOrderBy];
      const leftNum = Number(left);
      const rightNum = Number(right);
      if (Number.isFinite(leftNum) && Number.isFinite(rightNum)) {
        return leftNum - rightNum;
      }
      const leftStr = left == null ? "" : String(left);
      const rightStr = right == null ? "" : String(right);
      return leftStr.localeCompare(rightStr);
    };

    const stabilized = summaryRows.map((row, index) => ({ row, index }));
    stabilized.sort((a, b) => {
      const orderResult = compare(a.row, b.row);
      if (orderResult !== 0) {
        return summaryOrder === "asc" ? orderResult : -orderResult;
      }
      return a.index - b.index;
    });
    return stabilized.map((item) => item.row);
  }, [summaryRows, summaryOrder, summaryOrderBy]);

  const filteredSummaryRows = useMemo(() => {
    if (!sortedSummaryRows.length) return [];
    const limit = Number(summaryMinDeviation);
    if (!Number.isFinite(limit)) return sortedSummaryRows;
    return sortedSummaryRows.filter((row) => {
      const value = Number(row?.avgDeviationP);
      return Number.isFinite(value) && value >= limit;
    });
  }, [sortedSummaryRows, summaryMinDeviation]);

  const sortedPerformanceTableRows = useMemo(() => {
    if (!performanceTableRows.length) return [];
    const compare = (a, b) => {
      if (performanceTableOrderBy === "recordDate") {
        const leftDate = Date.parse(a?.recordDate);
        const rightDate = Date.parse(b?.recordDate);
        if (Number.isFinite(leftDate) && Number.isFinite(rightDate)) {
          return leftDate - rightDate;
        }
      }

      const left = a?.[performanceTableOrderBy];
      const right = b?.[performanceTableOrderBy];
      const leftNum = Number(left);
      const rightNum = Number(right);
      if (Number.isFinite(leftNum) && Number.isFinite(rightNum)) {
        return leftNum - rightNum;
      }
      const leftStr = left == null ? "" : String(left);
      const rightStr = right == null ? "" : String(right);
      return leftStr.localeCompare(rightStr);
    };

    const stabilized = performanceTableRows.map((row, index) => ({ row, index }));
    stabilized.sort((a, b) => {
      const orderResult = compare(a.row, b.row);
      if (orderResult !== 0) {
        return performanceTableOrder === "asc" ? orderResult : -orderResult;
      }
      return a.index - b.index;
    });
    return stabilized.map((item) => item.row);
  }, [performanceTableOrder, performanceTableOrderBy, performanceTableRows]);

  const handleSummaryRequestSort = (key) => {
    if (!key) return;
    if (summaryOrderBy === key) {
      setSummaryOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSummaryOrderBy(key);
      setSummaryOrder("asc");
    }
  };

  const handlePerformanceTableRequestSort = (key) => {
    if (!key) return;
    if (performanceTableOrderBy === key) {
      setPerformanceTableOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setPerformanceTableOrderBy(key);
      setPerformanceTableOrder("asc");
    }
  };

  const handleSubmit = async () => {
    const isSubscribed = await checkLocalInsightsSubscription();
    if (!isSubscribed) return;
    if (isOffline) {
      applyCachedReportsData(selectedFacilityId, selectedCycleId);
      return;
    }
    loadEnvTemp();
    loadLighting();
    loadPerformanceKpi();
    loadPerformanceWeightFcr();
    loadPerformanceActivities();
    loadTopIssuesEnvironment();
    loadTopIssuesPerformance();
    loadReportsFeed();
    loadSummary();
    loadPerformanceTable();
    loadFeedCosts();
    loadFeedSpend();
  };

  useEffect(() => {
    if (activitiesPayload) {
      buildActivitiesChartData(activitiesPayload, activitiesStatusFilters);
    }
  }, [activitiesPayload, activitiesStatusFilters]);

  const scrollToEnvPanel = (panelIndex) => {
    const panel = envPanelRefs.current[panelIndex];
    if (!panel || !envScrollRef.current) return;
    envScrollRef.current.scrollTo({ left: panel.offsetLeft, behavior: "smooth" });
    setActiveEnvPanel(panelIndex);
  };

  const scrollToPerfPanel = (panelIndex) => {
    const panel = perfPanelRefs.current[panelIndex];
    if (!panel || !perfScrollRef.current) return;
    perfScrollRef.current.scrollTo({ left: panel.offsetLeft, behavior: "smooth" });
    setActivePerfPanel(panelIndex);
  };

  const closeEnvTempPopup = () => {
    setEnvTempPopupOpen(false);
    setEnvTempPopupTitle("");
    setEnvTempPopupChartData(EMPTY_CHART);
    setEnvTempPopupError("");
    setIsLoadingEnvTempPopup(false);
  };

  const handleEnvTempPointClick = useCallback(
    async ({ dataset, label, dataIndex, metadata }) => {
      if (!canLoadSelectedReportData()) return;
      if (!label || dataIndex == null) return;
      if (!selectedFacilityId || !selectedCycleId) return;
      const requestDate = normalizePerformanceDateKey(metadata?.date);
      if (!requestDate) return;

      let requestUrl = "";
      let popupTitle = "";
      let popupErrorLabel = "";
      let popupConfig = null;

      if (dataset?.label === "Avg Temp") {
        requestUrl = tempEnvDayDetailUrl(
          selectedFacilityId,
          selectedCycleId,
          requestDate,
          requestDate
        );
        popupTitle = `Temp on D${label}`;
        popupErrorLabel = "temperature";
        popupConfig = {
          metricKey: "Temp",
          metricLabel: "Temp",
          chartLabel: "Temp",
          unit: " \u00B0C",
          borderColor: "#00d1ff",
          backgroundColor: "rgba(0, 209, 255, 0.2)",
        };
      } else if (dataset?.label === "Avg Hum") {
        requestUrl = humEnvDayDetailUrl(
          selectedFacilityId,
          selectedCycleId,
          requestDate,
          requestDate
        );
        popupTitle = `Hum on D${label}`;
        popupErrorLabel = "humidity";
        popupConfig = {
          metricKey: "Hum",
          metricLabel: "Hum",
          chartLabel: "Hum",
          unit: " %",
          borderColor: "#2dce89",
          backgroundColor: "rgba(45, 206, 137, 0.2)",
        };
      } else if (dataset?.label === "Avg FTemp") {
        requestUrl = floorTempEnvDayDetailUrl(
          selectedFacilityId,
          selectedCycleId,
          requestDate,
          requestDate
        );
        popupTitle = `Floor Temp on D${label}`;
        popupErrorLabel = "floor temperature";
        popupConfig = {
          metricKey: "Floor Temp",
          metricLabel: "Floor Temp",
          chartLabel: "Floor Temp",
          unit: " \u00B0C",
          borderColor: "#fb7185",
          backgroundColor: "rgba(251, 113, 133, 0.2)",
        };
      } else if (dataset?.label === "Avg Lux") {
        requestUrl = luxEnvDayDetailUrl(
          selectedFacilityId,
          selectedCycleId,
          requestDate,
          requestDate
        );
        popupTitle = `Lux on D${label}`;
        popupErrorLabel = "lux";
        popupConfig = {
          metricKey: "Lux",
          metricLabel: "Lux",
          chartLabel: "Lux",
          unit: " lx",
          borderColor: "#00d1ff",
          backgroundColor: "rgba(0, 209, 255, 0.2)",
        };
      } else if (dataset?.label === "Avg NH3") {
        requestUrl = nh3EnvDayDetailUrl(
          selectedFacilityId,
          selectedCycleId,
          requestDate,
          requestDate
        );
        popupTitle = `NH3 on D${label}`;
        popupErrorLabel = "NH3";
        popupConfig = {
          metricKey: "NH3",
          metricLabel: "NH3",
          chartLabel: "NH3",
          unit: " ppm",
          borderColor: "#fbcf33",
          backgroundColor: "rgba(251, 207, 51, 0.2)",
        };
      } else if (dataset?.label === "Avg Water Temp") {
        requestUrl = waterTempEnvDayDetailUrl(
          selectedFacilityId,
          selectedCycleId,
          requestDate,
          requestDate
        );
        popupTitle = `WTemp on D${label}`;
        popupErrorLabel = "water temperature";
        popupConfig = {
          metricKey: "Temp",
          metricLabel: "WTemp",
          chartLabel: "WTemp",
          unit: " \u00B0C",
          borderColor: "#00d1ff",
          backgroundColor: "rgba(0, 209, 255, 0.2)",
        };
      } else if (dataset?.label === "Avg pH") {
        requestUrl = phEnvDayDetailUrl(
          selectedFacilityId,
          selectedCycleId,
          requestDate,
          requestDate
        );
        popupTitle = `pH on D${label}`;
        popupErrorLabel = "pH";
        popupConfig = {
          metricKey: "PH",
          metricLabel: "pH",
          chartLabel: "pH",
          unit: "",
          borderColor: "#2dce89",
          backgroundColor: "rgba(45, 206, 137, 0.2)",
        };
      } else if (dataset?.label === "Avg ORP") {
        requestUrl = orpEnvDayDetailUrl(
          selectedFacilityId,
          selectedCycleId,
          requestDate,
          requestDate
        );
        popupTitle = `ORP on D${label}`;
        popupErrorLabel = "ORP";
        popupConfig = {
          metricKey: "ORP",
          metricLabel: "ORP",
          chartLabel: "ORP",
          unit: "",
          borderColor: "#fbcf33",
          backgroundColor: "rgba(251, 207, 51, 0.2)",
        };
      } else {
        return;
      }

      setEnvTempPopupOpen(true);
      setEnvTempPopupTitle(popupTitle);
      setEnvTempPopupChartData(EMPTY_CHART);
      setEnvTempPopupError("");
      setIsLoadingEnvTempPopup(true);

      try {
        const response = await axios.post(requestUrl, null, createSkipAuthConfig());
        const rows = extractRows(response);
        const nextChart = buildEnvDetailPopupChart(rows, popupConfig);
        if (!nextChart.labels.length) {
          setEnvTempPopupError(
            `No detailed ${popupErrorLabel} data is available for ${requestDate}.`
          );
          setEnvTempPopupChartData(EMPTY_CHART);
          return;
        }
        setEnvTempPopupChartData(nextChart);
      } catch (error) {
        console.error("Unable to load environmental detail popup data", error);
        setEnvTempPopupError(
          `Unable to load detailed ${popupErrorLabel} data for the selected point.`
        );
        setEnvTempPopupChartData(EMPTY_CHART);
      } finally {
        setIsLoadingEnvTempPopup(false);
      }
    },
    [canLoadSelectedReportData, selectedFacilityId, selectedCycleId]
  );

  useEffect(() => {
    setEnvTempPopupOpen(false);
    setEnvTempPopupTitle("");
    setEnvTempPopupChartData(EMPTY_CHART);
    setEnvTempPopupError("");
    setIsLoadingEnvTempPopup(false);
  }, [selectedFacilityId, selectedCycleId]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={4}>
          <MDTypography variant="h4">Reports</MDTypography>
          <MDTypography variant="body2" color="text">
            Compare production KPIs, monitor trend deviations, and export ready-to-share summaries.
          </MDTypography>
        </MDBox>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={4}>
                <Typography>Farm:</Typography>
                <Select
                  fullWidth
                  value={selectedFarmId ?? ""}
                  onChange={(e) => handleFarmChange(e.target.value)}
                  sx={{
                    height: "1cm",
                    "& .MuiOutlinedInput-root, & .MuiInputBase-root": {
                      height: "1cm !important",
                    },
                    "& .MuiSelect-select": {
                      minHeight: "1cm !important",
                      lineHeight: "1cm !important",
                      display: "flex",
                      alignItems: "center",
                      py: 0,
                    },
                  }}
                >
                  {farmOptions.map((option) => (
                    <MenuItem
                      key={option?.id ?? option?.name ?? option}
                      value={option?.id ?? option}
                    >
                      {option?.name ?? option}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography>Facility:</Typography>
                <Select
                  fullWidth
                  value={selectedFacilityId ?? ""}
                  onChange={(e) => handleFacilityChange(e.target.value)}
                  sx={{
                    height: "1cm",
                    "& .MuiOutlinedInput-root, & .MuiInputBase-root": {
                      height: "1cm !important",
                    },
                    "& .MuiSelect-select": {
                      minHeight: "1cm !important",
                      lineHeight: "1cm !important",
                      display: "flex",
                      alignItems: "center",
                      py: 0,
                    },
                  }}
                >
                  {facilityOptions.map((option) => (
                    <MenuItem
                      key={option?.id ?? option?.name ?? option}
                      value={option?.id ?? option}
                    >
                      {option?.name ?? option}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography>Cycle:</Typography>
                <Select
                  fullWidth
                  value={selectedCycleId ?? ""}
                  onChange={(e) => setSelectedCycleId(e.target.value)}
                  sx={{
                    height: "1cm",
                    "& .MuiOutlinedInput-root, & .MuiInputBase-root": {
                      height: "1cm !important",
                    },
                    "& .MuiSelect-select": {
                      minHeight: "1cm !important",
                      lineHeight: "1cm !important",
                      display: "flex",
                      alignItems: "center",
                      py: 0,
                    },
                  }}
                >
                  {cycleOptions.length ? (
                    cycleOptions.map((option) => (
                      <MenuItem
                        key={option?.id ?? option?.name ?? option}
                        value={option?.id ?? option}
                      >
                        {option?.name ?? option}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="">No cycles</MenuItem>
                  )}
                </Select>
                <MDBox sx={{ display: "flex", justifyContent: "flex-end", mt: "0.5cm" }}>
                  <Button
                    variant="contained"
                    color="info"
                    onClick={handleSubmit}
                    disabled={
                      isLoadingEnvTemp ||
                      isLoadingPerformance ||
                      isLoadingPerformanceWeightFcr ||
                      isLoadingActivities
                    }
                    sx={{ minHeight: "1cm" }}
                  >
                    Submit
                  </Button>
                </MDBox>
              </Grid>
            </Grid>
            <MDBox mb={1} sx={{ mt: "0.2cm" }}>
              <MDTypography variant="h6">Top 10 Issues</MDTypography>
            </MDBox>
            <Grid container spacing={3} sx={{ mt: "0.2cm" }}>
              <Grid item xs={12} md={6}>
                <MDBox mb={3} sx={{ height: "300px", mt: 0 }}>
                  <FeedChart
                    color="dark"
                    title="Environment"
                    chart={topIssuesEnvironmentChartData}
                    chartHeight="calc(12.5rem + 1.5cm)"
                    showTotal={false}
                    titleSx={{ mt: "0cm" }}
                  />
                </MDBox>
              </Grid>
              <Grid item xs={12} md={6}>
                <MDBox mb={3} sx={{ height: "300px", mt: 0 }}>
                  <FeedChart
                    color="dark"
                    title="Performance"
                    chart={topIssuesPerformanceChartData}
                    showTotal={false}
                    titleSx={{ mt: "0cm" }}
                    chartHeight="calc(12.5rem + 1.5cm)"
                  />
                </MDBox>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sx={{ mt: "0.5cm" }}>
            <MDBox mb={2} sx={{ mt: "0cm" }}>
              <MDTypography variant="h6" mb={0.5}>
                Performance
              </MDTypography>
            </MDBox>
            <MDBox position="relative">
              <MDBox
                sx={{
                  position: "absolute",
                  right: "1rem",
                  top: "-0.6cm",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  zIndex: 3,
                }}
              >
                {[0, 1, 2].map((idx) => (
                  <Tooltip
                    key={idx}
                    title={idx === 0 ? "Mortality" : idx === 1 ? "Weight/FCR" : "Activities"}
                    placement="top"
                  >
                    <MDBox
                      component="button"
                      type="button"
                      onClick={() => scrollToPerfPanel(idx)}
                      aria-label={
                        idx === 0
                          ? "Show mortality"
                          : idx === 1
                          ? "Show weight and FCR"
                          : "Show activities"
                      }
                      sx={{
                        width: "0.5rem",
                        height: "0.5rem",
                        borderRadius: "50%",
                        border: "none",
                        cursor: "pointer",
                        backgroundColor: activePerfPanel === idx ? "#1a1f37" : "#c6cbd3",
                        boxShadow:
                          activePerfPanel === idx ? "0 0 0 2px rgba(26, 31, 55, 0.2)" : "none",
                        p: 0,
                      }}
                    />
                  </Tooltip>
                ))}
              </MDBox>
              <MDBox
                ref={perfScrollRef}
                sx={{
                  display: "flex",
                  overflowX: "hidden",
                  overflowY: "hidden",
                  gap: "2cm",
                  touchAction: "pan-y",
                }}
              >
                <MDBox
                  ref={(el) => {
                    perfPanelRefs.current[0] = el;
                  }}
                  sx={{ minWidth: "100%" }}
                >
                  <MDBox position="relative">
                    <ReportsLineChart
                      color="secondary"
                      chart={performanceChartData}
                      chartHeight="calc(22rem + 1cm)"
                      cardSx={{
                        height: "100%",
                        pb: { xs: "1cm", sm: 0 },
                        mt: "0.7cm",
                        borderRadius: "12px",
                      }}
                      contentSx={{ pb: "2cm" }}
                      chartWrapperSx={{ position: "relative", top: "0cm" }}
                      showMinDeviation
                      minDeviationLabel="Min Deviation"
                      minDeviationValue={performanceMinDeviation}
                      onMinDeviationChange={(value) =>
                        setPerformanceMinDeviation(normalizeMinDeviation(value))
                      }
                      defaultEnabled={performanceMinDeviationDefaultEnabled}
                      onDefaultEnabledChange={(checked) => {
                        setPerformanceMinDeviationDefaultEnabled(checked);
                        if (!checked) setPerformanceMinDeviation(0);
                      }}
                      rightCheckboxItems={[]}
                    />
                    <MDBox
                      sx={{
                        position: "absolute",
                        right: "1rem",
                        bottom: "calc(0.75rem - 1.5cm + 1.5cm)",
                        display: "grid",
                        gridTemplateColumns: "auto auto",
                        gap: 1,
                        alignItems: "center",
                      }}
                    >
                      <MDTypography variant="button" color="text" sx={{ fontSize: "0.95rem" }}>
                        <MDBox component="span" sx={{ fontWeight: "bold" }}>
                          Live:
                        </MDBox>{" "}
                        {performanceMeta.live ?? "--"}
                      </MDTypography>
                      <MDTypography variant="button" color="text" sx={{ fontSize: "0.95rem" }}>
                        <MDBox component="span" sx={{ fontWeight: "bold" }}>
                          Cumul:
                        </MDBox>{" "}
                        {performanceMeta.cumulative ?? "--"}
                      </MDTypography>
                      <MDTypography
                        variant="button"
                        color="text"
                        sx={{ fontSize: "0.95rem", gridColumn: "1 / -1", mt: "-0.2cm" }}
                      >
                        <MDBox component="span" sx={{ fontWeight: "bold" }}>
                          Cycle:
                        </MDBox>{" "}
                        {performanceMeta.startDate && performanceMeta.endDate
                          ? `${performanceMeta.startDate} - ${performanceMeta.endDate}`
                          : "--"}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </MDBox>
                <MDBox
                  ref={(el) => {
                    perfPanelRefs.current[1] = el;
                  }}
                  sx={{ minWidth: "100%" }}
                >
                  <Card
                    sx={{
                      height: "100%",
                      mt: "1.2cm",
                      pb: { xs: "1cm", sm: 0 },
                      borderRadius: "12px",
                      boxShadow: "none",
                      border: "none",
                    }}
                  >
                    <MDBox padding="1rem" sx={{ position: "relative", mt: "-0.5cm" }}>
                      <MDBox
                        sx={{
                          position: "absolute",
                          right: "1rem",
                          top: "10.5cm",
                          zIndex: 2,
                        }}
                      >
                        <MDTypography variant="button" color="text" sx={{ fontSize: "0.95rem" }}>
                          <MDBox component="span" sx={{ fontWeight: "bold" }}>
                            Cycle:
                          </MDBox>{" "}
                          {performanceMeta.startDate && performanceMeta.endDate
                            ? `${performanceMeta.startDate} - ${performanceMeta.endDate}`
                            : "--"}
                        </MDTypography>
                      </MDBox>
                      <MDBox
                        sx={{
                          position: "absolute",
                          left: "1rem",
                          top: "10.5cm",
                          zIndex: 2,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <MDTypography variant="button" color="text" sx={{ fontSize: "0.95rem" }}>
                          Min Deviation
                        </MDTypography>
                        <Select
                          size="small"
                          value={performanceMinDeviation}
                          onChange={(e) =>
                            setPerformanceMinDeviation(normalizeMinDeviation(e.target.value))
                          }
                          sx={{
                            minWidth: "2.25rem",
                            "& .MuiSelect-select": { py: 0.5, fontSize: "0.95rem" },
                          }}
                        >
                          {Array.from({ length: 101 }, (_, idx) => (
                            <MenuItem key={idx} value={idx} sx={{ fontSize: "0.95rem" }}>
                              {idx}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              color="primary"
                              checked={performanceMinDeviationDefaultEnabled}
                              onChange={(e) => {
                                const nextChecked = e.target.checked;
                                setPerformanceMinDeviationDefaultEnabled(nextChecked);
                                if (!nextChecked) setPerformanceMinDeviation(0);
                              }}
                            />
                          }
                          label={
                            <MDTypography
                              variant="button"
                              color="text"
                              sx={{ fontSize: "0.95rem" }}
                            >
                              Default
                            </MDTypography>
                          }
                          sx={{
                            m: 0,
                            ml: "0.2cm",
                            "& .MuiFormControlLabel-label": { lineHeight: 1.3 },
                            "& .MuiCheckbox-root": { p: 0.5 },
                          }}
                        />
                      </MDBox>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <ReportsLineChart
                            color="secondary"
                            chart={performanceWeightChartData}
                            chartHeight="calc(22rem + 1cm)"
                            showCard={false}
                            cardSx={{
                              height: "100%",
                              pb: 0,
                              mt: 0,
                              borderRadius: 0,
                              boxShadow: "none",
                              backgroundColor: "transparent",
                              border: "none",
                            }}
                            contentSx={{ pb: "2cm" }}
                            chartWrapperSx={{ position: "relative", top: "0cm" }}
                            showMinDeviation={false}
                            rightCheckboxItems={[]}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <ReportsLineChart
                            color="secondary"
                            chart={performanceFcrChartData}
                            chartHeight="calc(22rem + 1cm)"
                            showCard={false}
                            cardSx={{
                              height: "100%",
                              pb: 0,
                              mt: 0,
                              borderRadius: 0,
                              boxShadow: "none",
                              backgroundColor: "transparent",
                              border: "none",
                            }}
                            contentSx={{ pb: "2cm" }}
                            chartWrapperSx={{ position: "relative", top: "0cm" }}
                            showMinDeviation={false}
                            rightCheckboxItems={[]}
                          />
                        </Grid>
                      </Grid>
                    </MDBox>
                  </Card>
                </MDBox>
                <MDBox
                  ref={(el) => {
                    perfPanelRefs.current[2] = el;
                  }}
                  sx={{ minWidth: "100%" }}
                >
                  <ReportsLineChart
                    color="secondary"
                    chart={activitiesChartData}
                    chartHeight="calc(22rem + 1cm)"
                    cardSx={{
                      height: "100%",
                      pb: { xs: "1cm", sm: 0 },
                      mt: "0.7cm",
                      borderRadius: "12px",
                    }}
                    contentSx={{ pb: "2cm" }}
                    chartWrapperSx={{ position: "relative", top: "0cm" }}
                    showMinDeviation={false}
                    rightCheckboxItems={["Completed", "Conditions not met", "Not completed"]}
                    onCheckedItemsChange={setActivitiesStatusFilters}
                  />
                </MDBox>
              </MDBox>
              {isLoadingPerformance || isLoadingActivities ? (
                <MDBox
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "transparent",
                    borderRadius: "12px",
                    zIndex: 2,
                  }}
                >
                  <CircularProgress color="info" />
                  <MDTypography
                    variant="button"
                    color="text"
                    sx={{
                      mt: 1,
                      letterSpacing: 0.2,
                      textTransform: "none",
                      fontSize: "1rem",
                      fontWeight: "bold",
                      color: "#e0e0e0",
                    }}
                  >
                    Loading data
                  </MDTypography>
                </MDBox>
              ) : null}
            </MDBox>
          </Grid>
          <Grid item xs={12} sx={{ mt: "0.5cm" }}>
            <MDBox mb={2} sx={{ mt: "0cm" }}>
              <MDTypography variant="h6" mb={0.5}>
                Environmental Conditions
              </MDTypography>
            </MDBox>
            <MDBox position="relative">
              <MDBox
                sx={{
                  position: "absolute",
                  right: "1rem",
                  top: "-0.6cm",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  zIndex: 3,
                }}
              >
                {[0, 1].map((idx) => (
                  <Tooltip
                    key={idx}
                    title={idx === 0 ? "Air Quality" : "Water Quality"}
                    placement="top"
                  >
                    <MDBox
                      component="button"
                      type="button"
                      onClick={() => scrollToEnvPanel(idx)}
                      aria-label={idx === 0 ? "Show air quality" : "Show water quality"}
                      sx={{
                        width: "0.5rem",
                        height: "0.5rem",
                        borderRadius: "50%",
                        border: "none",
                        cursor: "pointer",
                        backgroundColor: activeEnvPanel === idx ? "#1a1f37" : "#c6cbd3",
                        boxShadow:
                          activeEnvPanel === idx ? "0 0 0 2px rgba(26, 31, 55, 0.2)" : "none",
                        p: 0,
                      }}
                    />
                  </Tooltip>
                ))}
              </MDBox>
              <MDBox
                ref={envScrollRef}
                sx={{
                  display: "flex",
                  overflowX: "hidden",
                  overflowY: "hidden",
                  gap: "2cm",
                  pb: "0cm",
                  touchAction: "pan-y",
                }}
              >
                <MDBox
                  ref={(el) => {
                    envPanelRefs.current[0] = el;
                  }}
                  sx={{ minWidth: "100%" }}
                >
                  <ReportsLineChart
                    color="secondary"
                    chart={envTempChartData}
                    chartHeight="calc(22rem + 1cm)"
                    cardSx={{
                      height: "100%",
                      pb: { xs: "1cm", sm: 0 },
                      mt: "0.7cm",
                      borderRadius: "12px",
                    }}
                    contentSx={{ pb: "2cm" }}
                    chartWrapperSx={{ position: "relative", top: "0cm" }}
                    showMinDeviation
                    minDeviationLabel="Min Deviation"
                    minDeviationValue={envMinDeviation}
                    onMinDeviationChange={(value) =>
                      setEnvMinDeviation(normalizeMinDeviation(value))
                    }
                    defaultEnabled={envMinDeviationDefaultEnabled}
                    onDefaultEnabledChange={(checked) => {
                      setEnvMinDeviationDefaultEnabled(checked);
                      if (!checked) setEnvMinDeviation(0);
                    }}
                    onCheckedItemsChange={setEnvCheckedItems}
                    rightCheckboxItems={["Temp, Hum, NH3", "Floor Temp"]}
                    onPointClick={handleEnvTempPointClick}
                  />
                </MDBox>
                <MDBox
                  ref={(el) => {
                    envPanelRefs.current[1] = el;
                  }}
                  sx={{ minWidth: "100%" }}
                >
                  <ReportsLineChart
                    color="secondary"
                    chart={waterChartData}
                    chartHeight="calc(22rem + 1cm)"
                    cardSx={{
                      height: "100%",
                      pb: { xs: "1cm", sm: 0 },
                      mt: "0.7cm",
                      borderRadius: "12px",
                    }}
                    contentSx={{ pb: "2cm" }}
                    chartWrapperSx={{ position: "relative", top: "0cm" }}
                    showMinDeviation
                    minDeviationLabel="Min Deviation"
                    minDeviationValue={waterMinDeviation}
                    onMinDeviationChange={(value) =>
                      setWaterMinDeviation(normalizeMinDeviation(value))
                    }
                    defaultEnabled={waterMinDeviationDefaultEnabled}
                    onDefaultEnabledChange={(checked) => {
                      setWaterMinDeviationDefaultEnabled(checked);
                      if (!checked) setWaterMinDeviation(0);
                    }}
                    onCheckedItemsChange={setWaterCheckedItems}
                    rightCheckboxItems={["Water Temp", "pH", "ORP"]}
                    onPointClick={handleEnvTempPointClick}
                  />
                </MDBox>
              </MDBox>
              {isLoadingEnvTemp ? (
                <MDBox
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "transparent",
                    borderRadius: "12px",
                    zIndex: 2,
                  }}
                >
                  <CircularProgress color="info" />
                  <MDTypography
                    variant="button"
                    color="text"
                    sx={{
                      mt: 1,
                      letterSpacing: 0.2,
                      textTransform: "none",
                      fontSize: "1rem",
                      fontWeight: "bold",
                      color: "#e0e0e0",
                    }}
                  >
                    Loading data
                  </MDTypography>
                </MDBox>
              ) : null}
            </MDBox>
          </Grid>
          <Grid item xs={12} sx={{ mt: "0.5cm" }}>
            <MDBox mb={2} sx={{ mt: "0cm" }}>
              <MDTypography variant="h6" mb={0.5}>
                Lighting
              </MDTypography>
            </MDBox>
            <MDBox position="relative">
              <ReportsLineChart
                color="secondary"
                chart={lightingChartData}
                xAxisTickRotation={0}
                chartHeight="calc(22rem + 1cm)"
                cardSx={{
                  height: "100%",
                  pb: { xs: "1cm", sm: 0 },
                  mt: "0.7cm",
                  borderRadius: "12px",
                }}
                contentSx={{ pb: "2cm" }}
                chartWrapperSx={{ position: "relative", top: "0cm" }}
                showMinDeviation
                minDeviationLabel="Min Deviation"
                minDeviationValue={lightingMinDeviation}
                onMinDeviationChange={(value) =>
                  setLightingMinDeviation(normalizeMinDeviation(value))
                }
                defaultEnabled={lightingMinDeviationDefaultEnabled}
                onDefaultEnabledChange={(checked) => {
                  setLightingMinDeviationDefaultEnabled(checked);
                  if (!checked) setLightingMinDeviation(0);
                }}
                rightCheckboxItems={["Lux", "Dark Period"]}
                onCheckedItemsChange={setLightingCheckedItems}
                onPointClick={handleEnvTempPointClick}
              />
              {isLoadingEnvTemp ? (
                <MDBox
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "transparent",
                    borderRadius: "12px",
                    zIndex: 2,
                  }}
                >
                  <CircularProgress color="info" />
                  <MDTypography
                    variant="button"
                    color="text"
                    sx={{
                      mt: 1,
                      letterSpacing: 0.2,
                      textTransform: "none",
                      fontSize: "1rem",
                      fontWeight: "bold",
                      color: "#e0e0e0",
                    }}
                  >
                    Loading data
                  </MDTypography>
                </MDBox>
              ) : null}
            </MDBox>
          </Grid>

          <Grid item xs={12} md={6} sx={{ mt: "2cm" }}>
            <MDBox mb={3} sx={{ height: "300px", mt: 0 }}>
              <FeedChart
                color="info"
                title="Feed Consumption"
                chart={feedChartData}
                feed_total={feedTotal}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} sx={{ mt: "2cm" }}>
            <MDBox mb={3} sx={{ height: "300px", mt: 0 }}>
              <WaterChart
                color="info"
                title="Water Consumption"
                chart={waterConsumptionChartData}
                water_total={waterConsumptionTotal}
                chartHeight="12.5rem"
                totalLabel="Total Water"
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={4} md={3} sx={{ mt: "1cm", transform: "translateY(0.5cm)" }}>
            <Select
              fullWidth
              value={activeReportSection}
              onChange={(e) => setActiveReportSection(e.target.value)}
              sx={{
                height: "1cm",
                "& .MuiOutlinedInput-root, & .MuiInputBase-root": {
                  height: "1cm !important",
                },
                "& .MuiSelect-select": {
                  minHeight: "1cm !important",
                  lineHeight: "1cm !important",
                  display: "flex",
                  alignItems: "center",
                  py: 0,
                },
              }}
            >
              <MenuItem value="Performance">Performance</MenuItem>
              <MenuItem value="Environmental Conditions">Environmental Conditions</MenuItem>
              <MenuItem value="Costs">Costs</MenuItem>
            </Select>
          </Grid>
          <Grid
            item
            xs={12}
            sx={{
              mt: "1cm",
              order: 2,
              display: activeReportSection === "Costs" ? "block" : "none",
            }}
          >
            <Card sx={{ overflow: "hidden" }}>
              <MDBox p={2}>
                <MDTypography variant="subtitle2" sx={{ mt: 0, fontWeight: 700, color: "#344767" }}>
                  Feed
                </MDTypography>
                <TableContainer
                  component={MDBox}
                  sx={{
                    mt: 1,
                    boxShadow: "none",
                    width: "fit-content",
                    maxWidth: "100%",
                    mx: 0,
                    overflowX: "auto",
                    position: "relative",
                  }}
                >
                  <Table
                    size="small"
                    sx={{
                      tableLayout: "fixed",
                      width: "27.7cm",
                      minWidth: "27.7cm",
                      borderCollapse: "collapse",
                      "& th, & td": {
                        border: "1px solid",
                        borderColor: "grey.300",
                        fontSize: "0.9rem",
                        lineHeight: 1.2,
                        p: 0.5,
                        textAlign: "center",
                        color: "text.primary",
                      },
                    }}
                  >
                    <colgroup>
                      {feedCostReportColumns.map((column) => (
                        <col key={column.key} style={{ width: column.width }} />
                      ))}
                    </colgroup>
                    <TableBody>
                      <TableRow sx={{ height: "0.9cm", bgcolor: "grey.100" }}>
                        <TableCell rowSpan={2} sx={{ fontWeight: 700 }}>
                          <TableSortLabel
                            active={feedCostOrderBy === "date"}
                            direction={feedCostOrderBy === "date" ? feedCostOrder : "asc"}
                            onClick={() => handleFeedCostRequestSort("date")}
                            sx={{ "& .MuiTableSortLabel-icon": { opacity: 1 } }}
                          >
                            Date
                          </TableSortLabel>
                        </TableCell>
                        <TableCell rowSpan={2} sx={{ fontWeight: 700 }}>
                          Day
                        </TableCell>
                        <TableCell rowSpan={2} sx={{ fontWeight: 700 }}>
                          Stage
                        </TableCell>
                        <TableCell rowSpan={2} sx={{ fontWeight: 700 }}>
                          Feed Name
                        </TableCell>
                        <TableCell rowSpan={2} sx={{ fontWeight: 700 }}>
                          Supplier
                        </TableCell>
                        <TableCell rowSpan={2} sx={{ fontWeight: 700 }}>
                          Weight
                          <MDBox component="span" display="block">
                            (Kg)
                          </MDBox>
                        </TableCell>
                        <TableCell colSpan={2} sx={{ fontWeight: 700 }}>
                          Storage Topup
                        </TableCell>
                        <TableCell rowSpan={2} sx={{ fontWeight: 700 }}>
                          Cost
                          <MDBox component="span" display="block">
                            (ex VAT)
                          </MDBox>
                        </TableCell>
                        <TableCell rowSpan={2} sx={{ fontWeight: 700 }}>
                          Cost
                          <MDBox component="span" display="block">
                            (inc VAT)
                          </MDBox>
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{ height: "0.9cm", bgcolor: "grey.100" }}>
                        <TableCell sx={{ fontWeight: 700 }}>Bin1</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Bin2</TableCell>
                      </TableRow>
                      {isLoadingFeedCosts ? (
                        <TableRow>
                          <TableCell
                            colSpan={feedCostReportColumns.length}
                            align="center"
                            sx={{ py: 2 }}
                          >
                            <CircularProgress color="info" size={24} />
                          </TableCell>
                        </TableRow>
                      ) : feedCostsError ? (
                        <TableRow>
                          <TableCell
                            colSpan={feedCostReportColumns.length}
                            align="center"
                            sx={{ py: 2, color: "error.main" }}
                          >
                            {feedCostsError}
                          </TableCell>
                        </TableRow>
                      ) : sortedFeedCostRows.length ? (
                        <>
                          {sortedFeedCostRows.map((row) => (
                            <TableRow key={row.id} sx={{ height: "0.64cm" }}>
                              {feedCostReportColumns.map((column) => {
                                const value = row[column.key];
                                const isNumericColumn = [
                                  "weightKg",
                                  "bin1",
                                  "bin2",
                                  "costExVat",
                                  "costIncVat",
                                ].includes(column.key);
                                return (
                                  <TableCell key={column.key}>
                                    {isNumericColumn ? formatReportNumber(value) : value || ""}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          ))}
                          <TableRow
                            sx={{
                              height: "0.64cm",
                              bgcolor: "grey.100",
                              fontWeight: 700,
                              position: "sticky",
                              bottom: 0,
                              zIndex: 1,
                            }}
                          >
                            <TableCell colSpan={8} sx={{ textAlign: "right", fontWeight: 700 }}>
                              Total
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>
                              {formatReportNumber(feedCostTotalExVat)}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>
                              {formatReportNumber(feedCostTotalIncVat)}
                            </TableCell>
                          </TableRow>
                        </>
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={feedCostReportColumns.length}
                            align="center"
                            sx={{ py: 2, color: "text.secondary" }}
                          >
                            No feed cost data available.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </MDBox>
            </Card>
            <Card sx={{ overflow: "hidden", mt: 2 }}>
              <MDBox p={2}>
                <MDTypography variant="subtitle2" sx={{ mt: 0, fontWeight: 700, color: "#344767" }}>
                  DOC
                </MDTypography>
                <TableContainer
                  component={MDBox}
                  sx={{
                    mt: 1,
                    boxShadow: "none",
                    width: "fit-content",
                    maxWidth: "100%",
                    mx: 0,
                    overflowX: "auto",
                    position: "relative",
                  }}
                >
                  <Table
                    size="small"
                    sx={{
                      tableLayout: "fixed",
                      width: "30.2cm",
                      minWidth: "30.2cm",
                      borderCollapse: "collapse",
                      "& th, & td": {
                        border: "1px solid",
                        borderColor: "grey.300",
                        fontSize: "0.9rem",
                        lineHeight: 1.2,
                        p: 0.5,
                        textAlign: "center",
                        color: "text.primary",
                      },
                    }}
                  >
                    <colgroup>
                      {docCostReportColumns.map((column) => (
                        <col key={column.key} style={{ width: column.width }} />
                      ))}
                    </colgroup>
                    <TableBody>
                      <TableRow sx={{ height: "0.9cm", bgcolor: "grey.100" }}>
                        {docCostReportColumns.map((column) => (
                          <TableCell key={column.key} sx={{ fontWeight: 700 }}>
                            {column.key === "date" ? (
                              <TableSortLabel
                                active
                                direction={docCostOrder}
                                onClick={() =>
                                  setDocCostOrder((current) => (current === "asc" ? "desc" : "asc"))
                                }
                                sx={{ "& .MuiTableSortLabel-icon": { opacity: 1 } }}
                              >
                                {column.label}
                              </TableSortLabel>
                            ) : (
                              column.label
                            )}
                            {column.detail ? (
                              <MDBox component="span" display="block">
                                {column.detail}
                              </MDBox>
                            ) : null}
                          </TableCell>
                        ))}
                      </TableRow>
                      {isLoadingDocCosts ? (
                        <TableRow>
                          <TableCell
                            colSpan={docCostReportColumns.length}
                            align="center"
                            sx={{ py: 2 }}
                          >
                            <CircularProgress color="info" size={24} />
                          </TableCell>
                        </TableRow>
                      ) : docCostsError ? (
                        <TableRow>
                          <TableCell
                            colSpan={docCostReportColumns.length}
                            align="center"
                            sx={{ py: 2, color: "error.main" }}
                          >
                            {docCostsError}
                          </TableCell>
                        </TableRow>
                      ) : sortedDocCostRows.length ? (
                        <>
                          {sortedDocCostRows.map((row) => (
                            <TableRow key={row.id} sx={{ height: "0.64cm" }}>
                              {docCostReportColumns.map((column) => {
                                const value = row[column.key];
                                const isCostColumn = ["costExVat", "costIncVat"].includes(
                                  column.key
                                );
                                return (
                                  <TableCell key={column.key}>
                                    {isCostColumn ? formatReportNumber(value) : value ?? ""}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          ))}
                          <TableRow
                            sx={{
                              height: "0.64cm",
                              bgcolor: "grey.100",
                              fontWeight: 700,
                              position: "sticky",
                              bottom: 0,
                              zIndex: 1,
                            }}
                          >
                            <TableCell
                              colSpan={docCostReportColumns.length - 2}
                              sx={{ textAlign: "right", fontWeight: 700 }}
                            >
                              Total
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>
                              {formatReportNumber(docCostTotalExVat)}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>
                              {formatReportNumber(docCostTotalIncVat)}
                            </TableCell>
                          </TableRow>
                        </>
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={docCostReportColumns.length}
                            align="center"
                            sx={{ py: 2, color: "text.secondary" }}
                          >
                            No DOC cost data available.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </MDBox>
            </Card>
          </Grid>
          <Grid
            item
            xs={12}
            sx={{
              mt: "1cm",
              display: activeReportSection === "Performance" ? "block" : "none",
            }}
          >
            <Card sx={{ overflow: "hidden" }}>
              <MDBox p={2}>
                <MDTypography variant="h6">Performance</MDTypography>
                <TableContainer
                  component={MDBox}
                  sx={{
                    mt: 2,
                    boxShadow: "none",
                    width: "fit-content",
                    maxWidth: "100%",
                    mx: "auto",
                    overflowX: "auto",
                  }}
                >
                  <Table
                    size="small"
                    sx={{
                      tableLayout: "fixed",
                      width: "33.8cm",
                      minWidth: "33.8cm",
                      borderCollapse: "collapse",
                      "& th, & td": {
                        border: "1px solid",
                        borderColor: "grey.300",
                        fontSize: "0.9rem",
                        lineHeight: 1.2,
                        p: 0.5,
                        textAlign: "center",
                        color: "text.primary",
                      },
                    }}
                  >
                    <colgroup>
                      <col style={{ width: "2.6cm" }} />
                      <col style={{ width: "1.8cm" }} />
                      <col style={{ width: "2.2cm" }} />
                      <col style={{ width: "2.2cm" }} />
                      <col style={{ width: "2.2cm" }} />
                      <col style={{ width: "2.2cm" }} />
                      <col style={{ width: "2.4cm" }} />
                      <col style={{ width: "2.2cm" }} />
                      <col style={{ width: "2.2cm" }} />
                      <col style={{ width: "2.2cm" }} />
                      <col style={{ width: "2.2cm" }} />
                      <col style={{ width: "2.2cm" }} />
                      <col style={{ width: "2.2cm" }} />
                      <col style={{ width: "2.2cm" }} />
                    </colgroup>
                    <TableBody>
                      <TableRow sx={{ height: "0.9cm", bgcolor: "grey.100" }}>
                        <TableCell rowSpan={2} sx={{ fontWeight: 700 }}>
                          <TableSortLabel
                            active={performanceTableOrderBy === "recordDate"}
                            direction={
                              performanceTableOrderBy === "recordDate"
                                ? performanceTableOrder
                                : "asc"
                            }
                            onClick={() => handlePerformanceTableRequestSort("recordDate")}
                            sx={{ "& .MuiTableSortLabel-icon": { opacity: 1 } }}
                          >
                            Date
                          </TableSortLabel>
                        </TableCell>
                        <TableCell rowSpan={2} sx={{ fontWeight: 700 }}>
                          Day
                        </TableCell>
                        <TableCell colSpan={5} sx={{ fontWeight: 700 }}>
                          Mortality
                        </TableCell>
                        <TableCell colSpan={4} sx={{ fontWeight: 700 }}>
                          Weight
                        </TableCell>
                        <TableCell colSpan={3} sx={{ fontWeight: 700 }}>
                          FCR
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{ height: "0.9cm", bgcolor: "grey.100" }}>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <TableSortLabel
                            active={performanceTableOrderBy === "mortality"}
                            direction={
                              performanceTableOrderBy === "mortality"
                                ? performanceTableOrder
                                : "asc"
                            }
                            onClick={() => handlePerformanceTableRequestSort("mortality")}
                            sx={{ "& .MuiTableSortLabel-icon": { opacity: 1 } }}
                          >
                            Mortality
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Rate</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Cumul.</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Target</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <TableSortLabel
                            active={performanceTableOrderBy === "mortalityDeviation"}
                            direction={
                              performanceTableOrderBy === "mortalityDeviation"
                                ? performanceTableOrder
                                : "asc"
                            }
                            onClick={() => handlePerformanceTableRequestSort("mortalityDeviation")}
                            sx={{ "& .MuiTableSortLabel-icon": { opacity: 1 } }}
                          >
                            Dev%
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Day</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Avg</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Target</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <TableSortLabel
                            active={performanceTableOrderBy === "weightDeviation"}
                            direction={
                              performanceTableOrderBy === "weightDeviation"
                                ? performanceTableOrder
                                : "asc"
                            }
                            onClick={() => handlePerformanceTableRequestSort("weightDeviation")}
                            sx={{ "& .MuiTableSortLabel-icon": { opacity: 1 } }}
                          >
                            Dev%
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>FCR</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Target</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <TableSortLabel
                            active={performanceTableOrderBy === "fcrDeviation"}
                            direction={
                              performanceTableOrderBy === "fcrDeviation"
                                ? performanceTableOrder
                                : "asc"
                            }
                            onClick={() => handlePerformanceTableRequestSort("fcrDeviation")}
                            sx={{ "& .MuiTableSortLabel-icon": { opacity: 1 } }}
                          >
                            Dev%
                          </TableSortLabel>
                        </TableCell>
                      </TableRow>
                      {isLoadingPerformanceTable ? (
                        <TableRow>
                          <TableCell colSpan={14} align="center" sx={{ py: 2 }}>
                            <CircularProgress color="info" size={24} />
                          </TableCell>
                        </TableRow>
                      ) : sortedPerformanceTableRows.length ? (
                        sortedPerformanceTableRows.map((row, idx) => (
                          <TableRow
                            key={`${row.recordDate || "row"}-${idx}`}
                            sx={{ height: "0.64cm" }}
                          >
                            <TableCell>{formatSummaryValue(row.recordDate)}</TableCell>
                            <TableCell>{formatSummaryValue(row.day ?? row.weightDay)}</TableCell>
                            <TableCell>{formatSummaryValue(row.mortality)}</TableCell>
                            <TableCell>{formatSummaryValue(row.mortalityRate)}</TableCell>
                            <TableCell>{formatSummaryValue(row.mortalityCumul)}</TableCell>
                            <TableCell>{formatSummaryValue(row.mortalityTarget)}</TableCell>
                            <TableCell>{formatSummaryValue(row.mortalityDeviation)}</TableCell>
                            <TableCell>{formatSummaryValue(row.weightDay)}</TableCell>
                            <TableCell>{formatSummaryValue(row.weightAvg)}</TableCell>
                            <TableCell>{formatSummaryValue(row.weightTarget)}</TableCell>
                            <TableCell>{formatSummaryValue(row.weightDeviation)}</TableCell>
                            <TableCell>{formatSummaryValue(row.fcrValue)}</TableCell>
                            <TableCell>{formatSummaryValue(row.fcrTarget)}</TableCell>
                            <TableCell>{formatSummaryValue(row.fcrDeviation)}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={14}
                            align="center"
                            sx={{ py: 2, color: "text.secondary" }}
                          >
                            No performance data available.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </MDBox>
            </Card>
          </Grid>
          <Grid
            item
            xs={12}
            sx={{
              mt: "1cm",
              order: 1,
              display: activeReportSection === "Environmental Conditions" ? "block" : "none",
            }}
          >
            <Dialog open={envTempPopupOpen} onClose={closeEnvTempPopup} fullWidth maxWidth="lg">
              <DialogTitle sx={{ pr: 6 }}>
                {envTempPopupTitle || "Environmental Conditions"}
                <IconButton
                  aria-label="close"
                  onClick={closeEnvTempPopup}
                  sx={{ position: "absolute", right: 8, top: 8 }}
                >
                  <Icon>close</Icon>
                </IconButton>
              </DialogTitle>
              <DialogContent dividers>
                {isLoadingEnvTempPopup ? (
                  <MDBox
                    sx={{
                      minHeight: "10rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <CircularProgress size={28} />
                    <MDTypography variant="button" color="text">
                      Loading detailed environmental data
                    </MDTypography>
                  </MDBox>
                ) : envTempPopupError ? (
                  <MDBox sx={{ py: 4, textAlign: "center" }}>
                    <MDTypography variant="button" color="text">
                      {envTempPopupError}
                    </MDTypography>
                  </MDBox>
                ) : (
                  <MDBox sx={{ mt: 3 }}>
                    <ReportsLineChart
                      color="secondary"
                      chart={envTempPopupChartData}
                      chartHeight="22rem"
                      cardSx={{ boxShadow: "none" }}
                      contentSx={{ pb: 0 }}
                      chartWrapperSx={{ position: "relative", top: 0 }}
                    />
                  </MDBox>
                )}
              </DialogContent>
            </Dialog>
            <Card sx={{ overflow: "hidden" }}>
              <MDBox p={2}>
                <MDTypography variant="h6">Environmental Conditions</MDTypography>
                <TableContainer
                  component={MDBox}
                  sx={{
                    mt: 2,
                    boxShadow: "none",
                    width: "fit-content",
                    maxWidth: "100%",
                    mx: "auto",
                    overflowX: "auto",
                  }}
                >
                  <Table
                    size="small"
                    sx={{
                      tableLayout: "fixed",
                      width: "31.8cm",
                      minWidth: "31.8cm",
                      borderCollapse: "collapse",
                      "& th, & td": {
                        border: "1px solid",
                        borderColor: "grey.300",
                        fontSize: "0.9rem",
                        lineHeight: 1.2,
                        p: 0.5,
                        textAlign: "center",
                        color: "text.primary",
                      },
                    }}
                  >
                    <colgroup>
                      <col style={{ width: "2.6cm" }} />
                      <col style={{ width: "1.8cm" }} />
                      <col style={{ width: "2.4cm" }} />
                      <col style={{ width: "2.4cm" }} />
                      <col style={{ width: "2.8cm" }} />
                      <col style={{ width: "2.8cm" }} />
                      <col style={{ width: "2.2cm" }} />
                      <col style={{ width: "2.2cm" }} />
                      <col style={{ width: "2.2cm" }} />
                      <col style={{ width: "2.8cm" }} />
                      <col style={{ width: "2.8cm" }} />
                      <col style={{ width: "2.6cm" }} />
                    </colgroup>
                    <TableBody>
                      <TableRow sx={{ height: "0.96cm", bgcolor: "grey.100" }}>
                        {summaryColumns.map((column) => (
                          <TableCell
                            key={`heading-${column.key}`}
                            align={column.align}
                            sx={{ fontWeight: 700 }}
                          >
                            {column.sortable === false ? (
                              column.label
                            ) : (
                              <TableSortLabel
                                active={summaryOrderBy === column.key}
                                direction={summaryOrderBy === column.key ? summaryOrder : "asc"}
                                onClick={() => handleSummaryRequestSort(column.key)}
                                sx={{ "& .MuiTableSortLabel-icon": { opacity: 1 } }}
                              >
                                {column.label}
                              </TableSortLabel>
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                      {isLoadingSummary ? (
                        <TableRow>
                          <TableCell colSpan={summaryColumns.length} align="center" sx={{ py: 2 }}>
                            <CircularProgress color="info" size={24} />
                          </TableCell>
                        </TableRow>
                      ) : filteredSummaryRows.length ? (
                        filteredSummaryRows.map((row, idx) => (
                          <TableRow
                            key={`${row.recordDate || "row"}-${row.parametre || "param"}-${idx}`}
                            sx={{ height: "0.64cm" }}
                          >
                            {summaryColumns.map((column) => (
                              <TableCell key={column.key} align={column.align}>
                                {formatSummaryValue(row[column.key], column.decimals)}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={summaryColumns.length}
                            align="center"
                            sx={{ py: 2, color: "text.secondary" }}
                          >
                            No summary data available.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <MDBox
                  sx={{
                    mt: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    justifyContent: "flex-start",
                  }}
                >
                  <MDTypography variant="button" color="text" sx={{ fontSize: "0.95rem" }}>
                    Min Deviation
                  </MDTypography>
                  <Select
                    size="small"
                    value={summaryMinDeviation}
                    onChange={(e) => setSummaryMinDeviation(normalizeMinDeviation(e.target.value))}
                    sx={{
                      minWidth: "2.25rem",
                      "& .MuiSelect-select": { py: 0.5, fontSize: "0.95rem" },
                    }}
                  >
                    {Array.from({ length: 101 }, (_, idx) => (
                      <MenuItem key={idx} value={idx} sx={{ fontSize: "0.95rem" }}>
                        {idx}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        color="primary"
                        checked={summaryMinDeviationDefaultEnabled}
                        onChange={(e) => {
                          const nextChecked = e.target.checked;
                          setSummaryMinDeviationDefaultEnabled(nextChecked);
                          if (!nextChecked) setSummaryMinDeviation(0);
                        }}
                      />
                    }
                    label={
                      <MDTypography variant="button" color="text" sx={{ fontSize: "0.95rem" }}>
                        Default
                      </MDTypography>
                    }
                    sx={{
                      m: 0,
                      ml: "0.2cm",
                      "& .MuiFormControlLabel-label": { lineHeight: 1.3 },
                      "& .MuiCheckbox-root": { p: 0.5 },
                    }}
                  />
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Dialog
        open={Boolean(localInsightsMessage)}
        onClose={() => setLocalInsightsMessage("")}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pr: 6 }}>
          Local Insights
          <IconButton
            aria-label="close"
            onClick={() => setLocalInsightsMessage("")}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDTypography variant="body2" color="text">
            {localInsightsMessage}
          </MDTypography>
        </DialogContent>
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}

export default Reports;
